import { z } from 'zod';

import { ApiError, apiRequest, apiRequestBlob, apiStream, type StreamOptions } from '@/api/client';
import { permissionCatalogueSchema } from '@/api/permissions';
import {
  setupRunSchema,
  setupStoreSchema,
  setupSubdomainCheckSchema,
} from '@/features/assistant/api/setup';
import type { Theme } from '@/features/storefront/themeSchema';
import { pageSchema } from '@/api/types';
import { downloadBlob } from '@/components/data/download';
import {
  featureCatalogueSchema,
  featureTableSchema,
  featureValueSchema,
  type FeatureTable,
} from '@/features/billing/api/billing';
import {
  DEAL_DISPLAY_MODES,
  OFFER_STRIP_DIRECTIONS,
  OFFER_STRIP_SPEEDS,
  type DealDisplayMode,
} from '@/features/referrals/api/deals';
import { subdomainPolicySchema } from '@/features/store/api/store';
import { PLATFORM_STOREFRONT_DOMAIN } from '@/features/storefront/storefrontHost';

/**
 * Schemas mirroring `app/schemas/platform.py`.
 *
 * This is the staff surface: everything here reads or writes across merchant
 * boundaries and the backend gates all of it on a PLATFORM_* permission. The
 * client never sends a tenant id it was not given by one of these endpoints.
 */

// --- Merchants -------------------------------------------------------------

/**
 * ARCHIVED is included so the status filter can reach the merchants the
 * inactive-merchant cleanup retired. They are excluded from every other view -
 * the default listing, the active counts, the onboarding funnel - which is
 * what archiving means, so asking for them by name is the only way in.
 */
export const TENANT_STATUSES = [
  'PENDING',
  'ACTIVE',
  'SUSPENDED',
  'CANCELLED',
  'ARCHIVED',
] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

/**
 * Why a store was closed for non-payment: the hand-pay period ran out, a
 * mandate stayed past due beyond its grace period, or a staff-granted trial
 * ended. Mirrors the backend enum; kept as a string so a new reason renders
 * as its code instead of failing the whole page.
 */
export const BILLING_LOCK_REASONS = [
  'PERIOD_ENDED',
  'GRACE_EXPIRED',
  'TRIAL_ENDED',
  'PANEL_TRIAL_OVER',
] as const;
export type BillingLockReason = (typeof BILLING_LOCK_REASONS)[number];

export const BILLING_LOCK_REASON_LABELS: Record<BillingLockReason, string> = {
  PERIOD_ENDED: 'paid period ended',
  GRACE_EXPIRED: 'grace period expired',
  TRIAL_ENDED: 'trial ended',
  PANEL_TRIAL_OVER: 'free trial ended',
};

export function billingLockReasonLabel(reason: string): string {
  return (
    (BILLING_LOCK_REASON_LABELS as Record<string, string>)[reason] ??
    reason.replace(/_/g, ' ').toLowerCase()
  );
}

export const billingLockSchema = z.object({
  reason: z.string(),
  locked_at: z.string(),
  plan_code: z.string().nullable().optional().default(null),
  plan_name: z.string().nullable().optional().default(null),
  /** GST-inclusive renewal of the lapsed plan, or null when there is nothing to quote. */
  amount_due: z.string().nullable().optional().default(null),
  grace_ended_at: z.string().nullable().optional().default(null),
});

export type BillingLock = z.infer<typeof billingLockSchema>;

/**
 * Where a merchant's plan stands on renewal, in the order the server decides
 * it (first that applies wins). Mirrors `RenewalStatus` in
 * app/schemas/platform.py; the server works it out, this only names it.
 */
export const RENEWAL_STATUSES = [
  'LOCKED',
  'OVERDUE',
  'CANCELLING',
  'PENDING',
  'TRIAL',
  'DUE_SOON',
  'AUTO_RENEW',
  'MANUAL',
  'FREE',
] as const;

export type RenewalStatus = (typeof RENEWAL_STATUSES)[number];

/** The badge for each status: its words, the filter's words, and its colour. */
export const RENEWAL_DISPLAY: Record<
  RenewalStatus,
  { label: string; filter: string; tone: string; dated: string | null }
> = {
  LOCKED: {
    label: 'Expired',
    filter: 'Expired (store locked)',
    tone: 'bg-red-50 text-red-700 ring-red-200',
    dated: 'Locked',
  },
  OVERDUE: {
    label: 'Payment overdue',
    filter: 'Payment overdue',
    tone: 'bg-red-50 text-red-700 ring-red-200',
    dated: 'Grace until',
  },
  CANCELLING: {
    label: "Won't renew",
    filter: "Won't renew (cancelled)",
    tone: 'bg-amber-50 text-amber-700 ring-amber-200',
    dated: 'Ends',
  },
  PENDING: {
    label: 'Awaiting mandate',
    filter: 'Awaiting mandate',
    tone: 'bg-amber-50 text-amber-700 ring-amber-200',
    dated: null,
  },
  TRIAL: {
    label: 'Trial',
    filter: 'In trial',
    tone: 'bg-blue-50 text-blue-700 ring-blue-200',
    dated: 'Ends',
  },
  DUE_SOON: {
    label: 'Renewal due',
    filter: 'Renewal due (7 days)',
    tone: 'bg-amber-50 text-amber-700 ring-amber-200',
    dated: 'Due',
  },
  AUTO_RENEW: {
    label: 'Auto-renews',
    filter: 'Auto-renews',
    tone: 'bg-brand-50 text-brand-700 ring-brand-200',
    dated: 'Next charge',
  },
  MANUAL: {
    label: 'Renews manually',
    filter: 'Renews manually',
    tone: 'bg-surface text-ink-soft ring-line',
    dated: 'Period ends',
  },
  FREE: {
    label: 'Free',
    filter: 'Free (nothing to renew)',
    tone: 'bg-surface text-muted ring-line',
    dated: null,
  },
};

export const merchantSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  business_type: z.string(),
  email: z.string(),
  status: z.enum(TENANT_STATUSES),
  onboarding_step: z.string(),
  created_at: z.string(),
  plan_code: z.string(),
  product_count: z.number(),
  order_count: z.number(),
  gross_revenue: z.string(),
  /**
   * Set while the plan has ended and the store is closed to the merchant
   * until they choose a plan again (design §6). Distinct from SUSPENDED,
   * which is a staff decision: a billing lock clears itself on payment, or
   * through the unlock action below.
   */
  billing_lock: billingLockSchema.nullable().optional().default(null),
  /** Renewal at a glance; read leniently so an older server still loads the list. */
  renewal_status: z.enum(RENEWAL_STATUSES).catch('FREE').optional().default('FREE'),
  /** The date that goes with it (grace end, period end, trial end, next charge). */
  renewal_date: z.string().nullable().optional().default(null),
  /**
   * The address the storefront is served on. Not `slug`, which is made from
   * the business name and never changes; null until there is a store.
   */
  store_subdomain: z.string().nullable().optional().default(null),
});

/**
 * Where a merchant's storefront lives, as the platform screens show it: the
 * store's own address, or - before there is a store - the slug a new store
 * starts from. The merchant's own dashboard shows it the same way.
 */
export function merchantAddress(merchant: { slug: string; store_subdomain: string | null }): string {
  return `${merchant.store_subdomain ?? merchant.slug}.${PLATFORM_STOREFRONT_DOMAIN}`;
}

export const mandateHandoverSchema = z.object({
  state: z.string(),
  old_razorpay_subscription_id: z.string().nullable().optional().default(null),
  new_razorpay_subscription_id: z.string(),
  created_at: z.string(),
  completed_at: z.string().nullable().optional().default(null),
});

export const creditNoteSchema = z.object({
  number: z.string(),
  amount: z.string(),
  tax_amount: z.string(),
  against_invoice_number: z.string().nullable().optional().default(null),
  issued_at: z.string(),
});

export const platformSubscriptionSchema = z.object({
  plan_code: z.string(),
  plan_name: z.string(),
  active: z.boolean(),
  current_period_end: z.string().nullable(),
  trial_ends_at: z.string().nullable(),
  trial_started_at: z.string().nullable().optional().default(null),
  cancelled_at: z.string().nullable(),
  feature_overrides: z.record(z.unknown()),
  note: z.string().nullable(),
  // --- Recurring billing: the Razorpay mandate, when there is one ---
  billing_state: z.string().optional().default('MANUAL'),
  razorpay_subscription_id: z.string().nullable().optional().default(null),
  razorpay_plan_id: z.string().nullable().optional().default(null),
  provider_status: z.string().nullable().optional().default(null),
  next_billing_at: z.string().nullable().optional().default(null),
  current_period_start: z.string().nullable().optional().default(null),
  cancel_at_period_end: z.boolean().optional().default(false),
  grace_period_ends_at: z.string().nullable().optional().default(null),
  last_payment_error: z.string().nullable().optional().default(null),
  paid_cycles: z.number().optional().default(0),
  // --- Lapse and lock (design §6): why the plan stopped, and the grace that
  // ran before the lock. `grace_period_ends_at` above is reused for hand-pay
  // rows, so it is meaningful without a mandate.
  lapse_reason: z.string().nullable().optional().default(null),
  /** Prorated upgrades on a mandate: the old Razorpay subscription handed over to a new one (§5). */
  handovers: z.array(mandateHandoverSchema).optional().default([]),
  /** GST credit notes issued for unused days when the plan changed mid-period. */
  credit_notes: z.array(creditNoteSchema).optional().default([]),
});

export const merchantDetailSchema = merchantSummarySchema.extend({
  phone: z.string().nullable(),
  industry: z.string().nullable().optional().default(null),
  address_line1: z.string().nullable().optional().default(null),
  address_line2: z.string().nullable().optional().default(null),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postal_code: z.string().nullable().optional().default(null),
  country: z.string(),
  gstin: z.string().nullable(),
  legal_name: z.string().nullable().optional().default(null),
  fssai_number: z.string().nullable().optional().default(null),
  currency: z.string(),
  timezone: z.string(),
  onboarding_completed_at: z.string().nullable(),
  suspended_at: z.string().nullable(),
  suspension_reason: z.string().nullable(),
  internal_notes: z.string().nullable(),
  owner: z
    .object({
      id: z.string(),
      full_name: z.string(),
      email: z.string(),
      role: z.string(),
      status: z.string(),
      last_login_at: z.string().nullable(),
      /** A contact number only; not the number a WhatsApp owner signs in with. */
      phone: z.string().nullable().optional().default(null),
    })
    .nullable(),
  staff_count: z.number(),
  customer_count: z.number(),
  subscription: platformSubscriptionSchema.nullable(),
  /** The store's subdomain, its retired ones and the merchant's renames. Null before a store. */
  store_address: subdomainPolicySchema.nullable().optional().default(null),
});

export type MerchantSummary = z.infer<typeof merchantSummarySchema>;
export type MerchantDetail = z.infer<typeof merchantDetailSchema>;
export type PlatformSubscription = z.infer<typeof platformSubscriptionSchema>;
export type MandateHandover = z.infer<typeof mandateHandoverSchema>;
export type CreditNote = z.infer<typeof creditNoteSchema>;

export const MERCHANT_SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'revenue_desc', label: 'Highest revenue' },
  { value: 'orders_desc', label: 'Most orders' },
] as const;

export type MerchantSort = (typeof MERCHANT_SORTS)[number]['value'];

export interface MerchantListParams {
  page?: number;
  q?: string;
  status?: TenantStatus | '';
  plan?: string;
  sort?: MerchantSort;
  /** Only merchants whose store is closed for non-payment. */
  billingLocked?: boolean;
  /** Only merchants in this renewal state. */
  renewal?: RenewalStatus | '';
}

export function fetchMerchants(params: MerchantListParams = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.q) search.set('q', params.q);
  if (params.status) search.set('status', params.status);
  if (params.plan) search.set('plan', params.plan);
  if (params.sort) search.set('sort', params.sort);
  if (params.billingLocked) search.set('billing_locked', 'true');
  if (params.renewal) search.set('renewal', params.renewal);
  const query = search.toString();
  return apiRequest(
    `/platform/merchants${query ? `?${query}` : ''}`,
    pageSchema(merchantSummarySchema),
  );
}

export function fetchMerchant(tenantId: string) {
  return apiRequest(`/platform/merchants/${tenantId}`, merchantDetailSchema);
}

/**
 * A reason is required to suspend or cancel, and the backend enforces that.
 * It is shown to the merchant, so the form asks for an explanation rather than
 * an internal note - those are a separate field.
 */
export function changeMerchantStatus(
  tenantId: string,
  payload: { status: TenantStatus; reason?: string },
) {
  return apiRequest(`/platform/merchants/${tenantId}/status`, merchantDetailSchema, {
    method: 'PUT',
    body: payload,
  });
}

/**
 * The fields of the merchant's own Settings form, held to the same checks on
 * the server. Omitted fields are left alone; an empty phone, GSTIN, PIN, legal
 * name or FSSAI number clears it.
 */
export interface MerchantBusinessUpdate {
  name?: string;
  industry?: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  gstin?: string;
  legal_name?: string;
  fssai_number?: string;
  timezone?: string;
}

export function updateMerchantBusiness(tenantId: string, payload: MerchantBusinessUpdate) {
  return apiRequest(`/platform/merchants/${tenantId}`, merchantDetailSchema, {
    method: 'PATCH',
    body: payload,
  });
}

/**
 * A new email must be free (EMAIL_ALREADY_REGISTERED otherwise) and counts as
 * verified. An empty phone clears it.
 */
export interface MerchantOwnerUpdate {
  full_name?: string;
  email?: string;
  phone?: string;
}

export function updateMerchantOwner(tenantId: string, payload: MerchantOwnerUpdate) {
  return apiRequest(`/platform/merchants/${tenantId}/owner`, merchantDetailSchema, {
    method: 'PATCH',
    body: payload,
  });
}

/**
 * Clears a billing lock by hand, optionally extending the paid period so the
 * merchant is not locked again the moment the job next runs. Audited as
 * BILLING_UNLOCKED_BY_STAFF with the reason, which is why one is required.
 */
export function unlockMerchantBilling(
  tenantId: string,
  payload: { reason: string; extend_days?: number },
) {
  return apiRequest(`/platform/merchants/${tenantId}/billing/unlock`, merchantDetailSchema, {
    method: 'POST',
    body: payload,
  });
}

interface MerchantCreateBase {
  store_name: string;
  owner_name: string;
  /** A plan for them; not sent with `trial_days`. */
  plan_code?: string;
  /**
   * The /subscribe screen's free dashboard trial, this many days long, instead
   * of a plan. Left out with no plan too, the merchant chooses at first sign-in.
   */
  trial_days?: number;
  /** Left out: no industry, which the merchant picks in Settings. */
  industry?: string;
}

/** How the new owner will sign in. Exactly one, chosen when the account is opened. */
export type MerchantCreatePayload =
  | (MerchantCreateBase & {
      login_method: 'email_password';
      email: string;
      password: string;
      /** Make them replace the password at first sign-in. On unless sent as false. */
      require_change?: boolean;
    })
  | (MerchantCreateBase & { login_method: 'whatsapp'; phone: string; email?: string });

/**
 * Platform owner only. Opens the business and its owner together. A password
 * chosen here must be replaced by the merchant at their first sign-in, unless
 * `require_change` is sent as false.
 */
export function createMerchant(payload: MerchantCreatePayload) {
  return apiRequest('/platform/merchants', merchantDetailSchema, {
    method: 'POST',
    body: payload,
  });
}

/** The `start` event of a platform-run storefront setup. */
export const merchantSetupStartSchema = z.object({
  /** FULL_SETUP (with AI), or DEFAULT_SETUP when AI could not run. */
  action: z.string(),
  /** Why the AI setup gave way to the default one; null when it did not. */
  fallback_reason: z.string().nullable(),
  store_created: z.boolean(),
});

export type MerchantSetupStart = z.infer<typeof merchantSetupStartSchema>;

/**
 * The store the one-click setup would create for a business not yet added:
 * the first free address made from `name`, and the theme it picks for
 * `industry` - what the merchant's own setup window would suggest.
 */
export function fetchMerchantSetupSuggestion(name: string, industry: string) {
  const query = new URLSearchParams({ name, industry });
  return apiRequest(`/platform/merchants/storefront-setup/suggestion?${query}`, setupStoreSchema);
}

/** Whether a new merchant's store could live at `value`; `name` words the alternatives. */
export function checkMerchantSubdomain(value: string, name = '') {
  const query = new URLSearchParams({ value, name });
  return apiRequest(`/platform/merchants/subdomain-check?${query}`, setupSubdomainCheckSchema);
}

/** Where and how the store is created, when the setup has to create one. */
export interface MerchantStorefrontChoice {
  subdomain?: string;
  /** The server preset the store is built from. */
  theme_preset?: string;
  /** The theme's own page, built here as the dashboard builds it. */
  theme?: Theme;
}

/**
 * Platform owner only. Runs the merchant dashboard's one-click storefront setup
 * for `tenantId`, creating the store first if there is none - where and how
 * `choice` says, else as the setup suggests. Streams `start` and `step` events
 * to `onEvent`; resolves with the run, which the merchant can see and undo
 * from their own dashboard. Refused with INDUSTRY_REQUIRED for a merchant
 * without an industry, and SUBDOMAIN_TAKEN / SUBDOMAIN_RESERVED for an address
 * that cannot be had.
 */
export function runMerchantStorefrontSetup(
  tenantId: string,
  choice: MerchantStorefrontChoice = {},
  options: Pick<StreamOptions, 'signal' | 'onEvent'> = {},
) {
  return apiStream(
    `/platform/merchants/${tenantId}/storefront-setup`,
    z.object({ run: setupRunSchema }),
    { method: 'POST', body: choice, ...options },
  );
}

export function setMerchantNotes(tenantId: string, internalNotes: string | null) {
  return apiRequest(`/platform/merchants/${tenantId}/notes`, merchantDetailSchema, {
    method: 'PUT',
    body: { internal_notes: internalNotes },
  });
}

/** Staff rename: not held to the merchant limits, and not counted against them. */
export function renameMerchantSubdomain(tenantId: string, subdomain: string) {
  return apiRequest(`/platform/merchants/${tenantId}/subdomain`, merchantDetailSchema, {
    method: 'PUT',
    body: { subdomain },
  });
}

/** Frees a retired subdomain: it stops redirecting and any store may take it. */
export function releaseMerchantSubdomainAlias(tenantId: string, subdomain: string) {
  return apiRequest(
    `/platform/merchants/${tenantId}/subdomain-aliases/${encodeURIComponent(subdomain)}`,
    merchantDetailSchema,
    { method: 'DELETE' },
  );
}

// --- Plans -----------------------------------------------------------------

export const PLAN_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const;
export type PlanStatus = (typeof PLAN_STATUSES)[number];

export const BILLING_INTERVALS = ['MONTHLY', 'QUARTERLY', 'YEARLY'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/**
 * A plan's feature table, keyed by feature. Which keys exist - and what each
 * one means - comes from the feature catalogue (`fetchPlanFeatureCatalogue`),
 * never from a list kept here: a quota is a number or null for unlimited, a
 * switch is a boolean, and `'ADDON'` is a switch that is billed separately.
 */
export type PlanFeatures = FeatureTable;
export const planFeaturesSchema = featureTableSchema;

/** The feature catalogue as the platform panel reads it. */
export function fetchPlanFeatureCatalogue() {
  return apiRequest('/platform/plans/features', featureCatalogueSchema);
}

export const planSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  price_minor: z.number(),
  currency: z.string(),
  billing_interval: z.enum(BILLING_INTERVALS),
  /** Razorpay's `interval`: charge every N of `billing_interval`. */
  billing_interval_count: z.number().optional().default(1),
  /**
   * The free trial: a switch and a length, kept separately so the owner can
   * turn a trial off without losing the duration they settled on. Sold as a
   * Razorpay subscription starting when the trial ends.
   */
  trial_enabled: z.boolean().optional().default(false),
  trial_days: z.number(),
  status: z.enum(PLAN_STATUSES),
  is_public: z.boolean(),
  /** Sales-led (Enterprise): "Call Sales" instead of a price, no checkout. */
  contact_sales: z.boolean().optional().default(false),
  /** A negotiated plan made for one merchant, sold through its payment link. */
  reserved_tenant_id: z.string().nullable().optional().default(null),
  reserved_tenant_name: z.string().nullable().optional().default(null),
  /** The read-only features page this plan is shared through; null once archived. */
  share_path: z.string().nullable().optional().default(null),
  sort_order: z.number(),
  features: planFeaturesSchema,
  /**
   * `{ FEATURE_KEY: position }`, sparse: where this plan wants a feature
   * shown, when that differs from the catalogue's own order. Empty on a plan
   * nobody has reordered, which reads as "as the catalogue has it".
   */
  feature_order: z.record(z.number()).optional().default({}),
  created_at: z.string(),
  updated_at: z.string(),
  subscriber_count: z.number(),
  // --- Recurring billing ---
  /** The Razorpay plan collecting this price on this cycle, once linked. */
  razorpay_plan_id: z.string().nullable().optional().default(null),
  subscription_enabled: z.boolean().optional().default(false),
  grace_period_days: z.number().optional().default(7),
  /** Enabled, linked and priced above zero: merchants get a mandate. */
  is_recurring: z.boolean().optional().default(false),
  // --- Optional "Store setup by NeonCarts team" add-on ---
  /** One-time fee in paise, charged with a new merchant's first payment. 0 hides it. */
  setup_fee_minor: z.number().optional().default(0),
  /** Products the setup team promises, at most. Not a plan limit. */
  setup_max_products: z.number().optional().default(50),
  setup_max_images_per_product: z.number().optional().default(5),
  /** Team time the fee buys, granted as setup-assistance hours on payment. */
  setup_hours: z.number().optional().default(12),
});

export type Plan = z.infer<typeof planSchema>;

export interface PlanPayload {
  code?: string;
  name?: string;
  description?: string | null;
  price_minor?: number;
  currency?: string;
  billing_interval?: BillingInterval;
  billing_interval_count?: number;
  trial_enabled?: boolean;
  trial_days?: number;
  status?: PlanStatus;
  is_public?: boolean;
  contact_sales?: boolean;
  /** `null` releases the plan from the merchant it was made for. */
  reserved_tenant_id?: string | null;
  sort_order?: number;
  features?: PlanFeatures;
  /** `null` unlinks - required alongside a price or cycle change on a linked plan. */
  razorpay_plan_id?: string | null;
  subscription_enabled?: boolean;
  grace_period_days?: number;
  setup_fee_minor?: number;
  setup_max_products?: number;
  setup_max_images_per_product?: number;
  setup_hours?: number;
}

export function fetchPlans() {
  return apiRequest('/platform/plans', z.array(planSchema));
}

export function createPlan(payload: PlanPayload) {
  return apiRequest('/platform/plans', planSchema, { method: 'POST', body: payload });
}

export function updatePlan(planId: string, payload: PlanPayload) {
  return apiRequest(`/platform/plans/${planId}`, planSchema, {
    method: 'PATCH',
    body: payload,
  });
}

export function deletePlan(planId: string) {
  return apiRequest(`/platform/plans/${planId}`, z.null(), { method: 'DELETE' });
}

// --- Plans as a spreadsheet --------------------------------------------------

/** Every plan and its feature table as an .xlsx, to edit offline and import back. */
export function exportPlansWorkbook() {
  return apiRequestBlob('/platform/plans/export');
}

/** Download the current catalogue. Resolves once the browser has the file. */
export async function downloadPlansWorkbook(): Promise<void> {
  const blob = await exportPlansWorkbook();
  downloadBlob(blob, plansWorkbookFilename());
}

/** `plans-20261008-1430.xlsx`, matching the name the server suggests. */
export function plansWorkbookFilename(now = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const day = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  return `plans-${day}-${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
}

export const PLAN_IMPORT_ACTIONS = ['create', 'update', 'unchanged', 'error'] as const;
export type PlanImportAction = (typeof PLAN_IMPORT_ACTIONS)[number];

const planImportRowSchema = z.object({
  /** The row on the Plans sheet, for pointing the owner back at it. */
  row: z.number(),
  code: z.string(),
  name: z.string().nullable(),
  action: z.enum(PLAN_IMPORT_ACTIONS),
  subscribers: z.number(),
  changes: z.array(
    z.object({ field: z.string(), label: z.string(), before: z.string().nullable(), after: z.string().nullable() }),
  ),
  /** Each names its cell, e.g. `Plans!G4 (Price) must be a number.` */
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  /** Edited in the panel after the workbook was exported. */
  stale: z.boolean(),
});
export type PlanImportRow = z.infer<typeof planImportRowSchema>;

const planImportReportSchema = z.object({
  dry_run: z.boolean(),
  applied: z.boolean(),
  exported_at: z.string().nullable(),
  /** Problems with the file as a whole. */
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  plans: z.array(planImportRowSchema),
  created: z.number(),
  updated: z.number(),
  unchanged: z.number(),
  failed: z.number(),
  stale: z.number(),
});
export type PlanImportReport = z.infer<typeof planImportReportSchema>;

/**
 * Upload an edited workbook. With `dryRun` the server reports what would
 * change and saves nothing; without it every row is saved together or, if any
 * row has a problem, none is.
 */
export function importPlansWorkbook(file: File, options: { dryRun: boolean; overwriteNewer?: boolean }) {
  const form = new FormData();
  form.append('file', file);
  form.append('dry_run', options.dryRun ? 'true' : 'false');
  form.append('overwrite_newer', options.overwriteNewer ? 'true' : 'false');
  return apiRequest('/platform/plans/import', planImportReportSchema, { method: 'POST', body: form });
}

const PLAN_IMPORT_ERRORS: Record<string, string> = {
  PLAN_IMPORT_INVALID_FILE: 'That file is not a plans workbook. Choose an .xlsx made by Export to Excel.',
  PLAN_IMPORT_TOO_LARGE: 'That file is too large to be a plans workbook.',
  EMPTY_UPLOAD: 'The file is empty.',
};

export function planImportErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (error instanceof ApiError) {
    if (error.status === 403) return 'You do not have permission to change plans.';
    return PLAN_IMPORT_ERRORS[error.code] ?? error.message;
  }
  return 'Something went wrong. Try again.';
}

/**
 * Create the Razorpay plan for this plan's price and cycle and link it. Once
 * per billing configuration - never per merchant - which is why it is a
 * button on the plan rather than anything the checkout does.
 */
export function createRazorpayPlan(planId: string) {
  return apiRequest(`/platform/plans/${planId}/razorpay-plan`, planSchema, { method: 'POST' });
}

// --- The feature catalogue -------------------------------------------------
//
// A feature is a *row* of the comparison table; a plan's `features` are the
// cells. These calls administer the rows - what exists, what it is called,
// where it sits, whether it is shown - while assignment stays with the plan,
// because whether a plan grants a feature is an entitlement and not a label.

export const PLAN_FEATURE_KINDS = ['SWITCH', 'QUOTA'] as const;
export type PlanFeatureKind = (typeof PLAN_FEATURE_KINDS)[number];

export const PLAN_FEATURE_STATUSES = ['ACTIVE', 'INTERNAL', 'ARCHIVED'] as const;
export type PlanFeatureStatus = (typeof PLAN_FEATURE_STATUSES)[number];

export const PLAN_FEATURE_STATUS_LABELS: Record<PlanFeatureStatus, string> = {
  ACTIVE: 'Live',
  INTERNAL: 'Internal',
  ARCHIVED: 'Archived',
};

export const PLAN_FEATURE_STATUS_HINTS: Record<PlanFeatureStatus, string> = {
  ACTIVE: 'Shown on the pricing page and on every merchant’s billing page.',
  INTERNAL: 'Configurable here, hidden from merchants. For preparing a feature before it ships.',
  ARCHIVED: 'Retired. Hidden everywhere; what each plan stored for it is left untouched.',
};

export const planFeatureSchema = z.object({
  id: z.string(),
  /** The stable machine key every plan's feature table addresses this by. */
  key: z.string(),
  label: z.string(),
  description: z.string(),
  category: z.string(),
  category_label: z.string().optional().default(''),
  kind: z.enum(PLAN_FEATURE_KINDS),
  unit: z.string().nullable(),
  display_unit: z.string().nullable(),
  display_scale: z.number(),
  per_month: z.boolean(),
  addon_capable: z.boolean(),
  /** Whether the API actually refuses requests over this, or it is display-only. */
  enforced: z.boolean(),
  counted: z.boolean(),
  status: z.enum(PLAN_FEATURE_STATUSES),
  sort_order: z.number(),
  /** True when the shipped code knows it by name: fixed shape, cannot be deleted. */
  is_builtin: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  /** The plans that grant it today, by code. Derived from the plan rows. */
  assigned_plan_codes: z.array(z.string()).default([]),
});
export type PlanFeature = z.infer<typeof planFeatureSchema>;

export interface PlanFeaturePayload {
  key?: string;
  label?: string;
  description?: string;
  category?: string;
  kind?: PlanFeatureKind;
  unit?: string | null;
  display_unit?: string | null;
  display_scale?: number;
  per_month?: boolean;
  addon_capable?: boolean;
  status?: PlanFeatureStatus;
  sort_order?: number;
  /** Plan codes to switch a brand-new feature on for, in the same request. */
  assign_to?: string[];
}

export const planFeatureImpactSchema = z.object({
  key: z.string(),
  label: z.string(),
  is_builtin: z.boolean(),
  plans: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      status: z.enum(PLAN_STATUSES),
      subscriber_count: z.number(),
      value: featureValueSchema.optional().default(null),
    }),
  ),
  affected_merchants: z.number(),
  overridden_subscriptions: z.number(),
});
export type PlanFeatureImpact = z.infer<typeof planFeatureImpactSchema>;

/** Every feature, in display order, including ones merchants are not shown. */
export function fetchPlanFeatures() {
  return apiRequest('/platform/plans/feature-catalogue', z.array(planFeatureSchema));
}

export function createPlanFeature(payload: PlanFeaturePayload) {
  return apiRequest('/platform/plans/feature-catalogue', planFeatureSchema, {
    method: 'POST',
    body: payload,
  });
}

export function updatePlanFeature(featureId: string, payload: PlanFeaturePayload) {
  return apiRequest(`/platform/plans/feature-catalogue/${featureId}`, planFeatureSchema, {
    method: 'PATCH',
    body: payload,
  });
}

export function deletePlanFeature(featureId: string) {
  return apiRequest(`/platform/plans/feature-catalogue/${featureId}`, z.null(), {
    method: 'DELETE',
  });
}

/**
 * What deleting this feature would take away. Read before the confirmation is
 * drawn, so the owner is told which plans carry it *before* they agree.
 */
export function fetchPlanFeatureImpact(featureId: string) {
  return apiRequest(
    `/platform/plans/feature-catalogue/${featureId}/impact`,
    planFeatureImpactSchema,
  );
}

/**
 * Set the catalogue's order, keys first to last. Writes only the order - a
 * drag can never change what a plan grants.
 */
export function reorderPlanFeatures(keys: string[]) {
  return apiRequest('/platform/plans/feature-catalogue/reorder', z.array(planFeatureSchema), {
    method: 'POST',
    body: { keys },
  });
}

/**
 * Switch features on or off for one plan. Unassigning removes the association
 * and nothing else: the feature itself, and every other plan, are untouched.
 */
export function assignPlanFeatures(
  planId: string,
  payload: { keys: string[]; assigned: boolean; value?: number | null },
) {
  return apiRequest(`/platform/plans/${planId}/features/assign`, planSchema, {
    method: 'POST',
    body: payload,
  });
}

/** The order one plan shows its features in. An empty list clears the override. */
export function setPlanFeatureOrder(planId: string, keys: string[]) {
  return apiRequest(`/platform/plans/${planId}/feature-order`, planSchema, {
    method: 'PUT',
    body: { keys },
  });
}

// --- Platform settings -----------------------------------------------------

/**
 * How the owner has asked the product to behave - policy that is meant to stay
 * put, unlike the flags panel, which is for things that are wrong today.
 */
export const platformSettingsSchema = z.object({
  /**
   * Whether `/subscribe` offers a new merchant a free trial of the dashboard
   * before they choose a plan. Off makes every new merchant choose a plan
   * first; trials already running carry on.
   */
  signup_skip_allowed: z.boolean(),
  /** How many days that trial lasts. Read when a trial starts. */
  panel_trial_days: z.number().optional().default(7),
  /**
   * How long a merchant must be dormant before the inactive-merchant cleanup
   * will offer to archive them. Read by the cleanup on every preview and again
   * at execution, so a change here takes effect on the next run.
   */
  inactive_merchant_days: z.number().optional().default(90),
  /**
   * Which billing cadence the public pricing page and the subscribe chooser
   * open on. Commercial policy rather than a property of the plans: without
   * it the tab a visitor lands on is whichever cycle happens to be cheapest.
   * The owner may name a cadence they sell nothing on - that is how the switch
   * is prepared before the plans are published - and those screens fall back
   * to the cheapest cycle actually on sale until there is one to show.
   */
  default_billing_interval: z.enum(BILLING_INTERVALS).optional().default('QUARTERLY'),
  /**
   * What the platform adds to the registrar's price for a domain. Here rather
   * than in the deployment's environment because it is commercial policy: the
   * owner changes it and the next search reprices, with nothing to redeploy.
   * The default is 8 rather than 0 on purpose - a margin whose failure mode is
   * selling at cost is worse than no setting.
   */
  domain_price_markup_percent: z.coerce.number().optional().default(8),
  /** Per-TLD exceptions to it, as the `io:10,tech:12` string the owner types. */
  domain_price_markup_overrides: z.string().optional().default(''),
  /** The least margin, in rupees, a single year must earn whatever the percentage says. */
  domain_price_markup_min: z.coerce.number().optional().default(0),
  /** Prices round *up* to a multiple of this, so merchants read a price tag. 0 keeps the paisa. */
  domain_price_rounding: z.coerce.number().optional().default(0),
  /**
   * The prepaid registrar float below which domains stop being sold. Below it
   * a purchase is refused outright rather than charged and then refunded.
   */
  registrar_balance_minimum: z.coerce.number().optional().default(5000),
  /**
   * What each AI Image Studio operation costs in credits, keyed by creative
   * type plus `HD_UPGRADE`. The server always sends a complete table - it fills
   * any key the stored blob is missing from the shipped defaults - so the panel
   * can render a row per operation without knowing which have ever been saved.
   */
  ai_image_credit_costs: z.record(z.number()).optional().default({}),
  /**
   * The AI credit packs a merchant can buy, smallest first - each with its own
   * price in rupees, as the decimal string the API sends. An empty list means
   * nothing is on sale.
   */
  ai_credit_packs: z
    .array(z.object({ credits: z.number(), price: z.coerce.string() }))
    .optional()
    .default([]),
  /** The smallest and largest pack the owner will sell. */
  ai_credit_pack_min: z.number().optional().default(50),
  ai_credit_pack_max: z.number().optional().default(5000),
  /** What one AI draft costs from bought credits once the monthly drafts are used. */
  ai_text_generation_credit_cost: z.number().optional().default(1),
  /** How far down a monthly allowance, in percent, a merchant is warned. */
  ai_credit_low_balance_percent: z.number().optional().default(20),
  /** The "how to set up Razorpay" link on the merchant payments screen; empty hides it. */
  razorpay_setup_guide_url: z.string().optional().default(''),
  /** The "how to set up" link on each provider's card on the business email screen; empty hides it. */
  microsoft365_setup_guide_url: z.string().optional().default(''),
  google_workspace_setup_guide_url: z.string().optional().default(''),
  zoho_mail_setup_guide_url: z.string().optional().default(''),
  /** Whether merchants see "Terms & Policies" in their dashboard menu. Hidden by default. */
  policies_menu_visible: z.boolean().optional().default(false),
  /** Whether the sign-in pages offer "Continue with WhatsApp". Hidden by default. */
  whatsapp_login_visible: z.boolean().optional().default(false),
  /** The marketing header's offer strip: colour and motion. */
  offer_strip_background: z.string().optional().default('#0b1220'),
  offer_strip_scroll: z.boolean().optional().default(true),
  offer_strip_direction: z.enum(OFFER_STRIP_DIRECTIONS).catch('LEFT_TO_RIGHT').default('LEFT_TO_RIGHT'),
  offer_strip_speed: z.enum(OFFER_STRIP_SPEEDS).catch('NORMAL').default('NORMAL'),
  /** Days a merchant waits between renaming their store's subdomain. */
  subdomain_rename_cooldown_days: z.number().optional().default(30),
  /** How many times a merchant may rename it. 0 means only staff can. */
  subdomain_rename_max: z.number().optional().default(3),
  /**
   * GST on plan purchases, added on top of the plan price. Off by default.
   * Changing what a new purchase carries - the switch or the rate - unlinks
   * every Razorpay plan, because a Razorpay plan's amount includes the GST.
   */
  gst_enabled: z.boolean().optional().default(false),
  /** The percentage, sent by the API as a decimal string. */
  gst_rate: z.coerce.number().optional().default(18),
  /**
   * The platform's own GST particulars for the tax invoice. Empty means not
   * given; without a GSTIN merchants download a payment receipt instead.
   */
  platform_gstin: z.string().optional().default(''),
  platform_legal_name: z.string().optional().default(''),
  platform_address: z.string().optional().default(''),
  platform_state: z.string().optional().default(''),
  sac_code: z.string().optional().default('998314'),
});

/** The bounds the API enforces. Mirrored here so the field can say so first. */
export const MIN_PANEL_TRIAL_DAYS = 1;
export const MAX_PANEL_TRIAL_DAYS = 30;
export const MIN_INACTIVE_MERCHANT_DAYS = 30;
export const MAX_INACTIVE_MERCHANT_DAYS = 1095;
export const MIN_SUBDOMAIN_RENAME_COOLDOWN_DAYS = 0;
export const MAX_SUBDOMAIN_RENAME_COOLDOWN_DAYS = 365;
export const MIN_GST_RATE = 0;
export const MAX_GST_RATE = 28;
export const MIN_SUBDOMAIN_RENAME_MAX = 0;
export const MAX_SUBDOMAIN_RENAME_MAX = 50;
export const MIN_DOMAIN_MARKUP_PERCENT = 0;
export const MAX_DOMAIN_MARKUP_PERCENT = 500;
export const MIN_AI_IMAGE_CREDIT_COST = 0;
export const MAX_AI_IMAGE_CREDIT_COST = 100;
export const MIN_AI_CREDIT_PACK_SIZE = 1;
export const MAX_AI_CREDIT_PACK_SIZE = 100_000;
export const MAX_AI_CREDIT_PACKS = 8;
export const MIN_AI_TEXT_GENERATION_CREDIT_COST = 1;
export const MAX_AI_TEXT_GENERATION_CREDIT_COST = 20;
export const MIN_AI_CREDIT_LOW_BALANCE_PERCENT = 1;
export const MAX_AI_CREDIT_LOW_BALANCE_PERCENT = 90;

/**
 * The AI Image Studio operations, in the order the panel lists them.
 *
 * Mirrors `DEFAULT_AI_IMAGE_CREDIT_COSTS` in
 * `app/models/platform_setting.py` - the server refuses a key it does not
 * know, so this list and that one have to agree.
 */
export const AI_IMAGE_OPERATIONS: { key: string; label: string; hint: string }[] = [
  { key: 'PRODUCT', label: 'Product image', hint: 'Full set of 3' },
  { key: 'BANNER', label: 'Store banner', hint: 'Full set of 3' },
  { key: 'CATEGORY', label: 'Category image', hint: 'Full set of 3' },
  { key: 'PROMOTIONAL', label: 'Promotional creative', hint: 'Full set of 3' },
  { key: 'CART', label: 'Cart creative', hint: 'Full set of 3' },
  { key: 'SOCIAL', label: 'Social media creative', hint: 'Full set of 3' },
  { key: 'PROFILE', label: 'Profile / brand image', hint: 'Full set of 3' },
  { key: 'HERO', label: 'Store hero', hint: 'Full set of 3' },
  { key: 'HD_UPGRADE', label: 'HD upgrade', hint: 'One image, full quality' },
];

export type PlatformSettings = z.infer<typeof platformSettingsSchema>;

export function fetchPlatformSettings() {
  return apiRequest('/platform/settings', platformSettingsSchema);
}

/** Only the settings being changed travel; an omitted one is left alone. */
export function updatePlatformSettings(payload: Partial<PlatformSettings>) {
  return apiRequest('/platform/settings', platformSettingsSchema, {
    method: 'PUT',
    body: payload,
  });
}

// --- Subscriptions ---------------------------------------------------------

export interface SubscriptionAssignPayload {
  plan_code: string;
  trial_days?: number;
  feature_overrides?: PlanFeatures;
  note?: string;
}

export function assignSubscription(tenantId: string, payload: SubscriptionAssignPayload) {
  return apiRequest(
    `/platform/merchants/${tenantId}/subscription`,
    platformSubscriptionSchema,
    { method: 'PUT', body: payload },
  );
}

export function cancelSubscription(tenantId: string) {
  return apiRequest(
    `/platform/merchants/${tenantId}/subscription`,
    platformSubscriptionSchema,
    { method: 'DELETE' },
  );
}

export const SUBSCRIPTION_INVOICE_STATUSES = ['PENDING', 'PAID', 'FAILED', 'CANCELLED'] as const;
export type SubscriptionInvoiceStatus = (typeof SUBSCRIPTION_INVOICE_STATUSES)[number];

export const subscriptionInvoiceSchema = z.object({
  id: z.string(),
  plan_code: z.string(),
  plan_name: z.string(),
  amount: z.string(),
  currency: z.string(),
  status: z.enum(SUBSCRIPTION_INVOICE_STATUSES),
  period_start: z.string(),
  period_end: z.string(),
  paid_at: z.string().nullable(),
  failure_reason: z.string().nullable(),
  created_at: z.string(),
  provider: z.string(),
  provider_payment_id: z.string().nullable(),
  kind: z.string().optional(),
  razorpay_subscription_id: z.string().nullable().optional(),
  setup_fee_amount: z.string().nullable().optional(),
  due_at: z.string().nullable().optional(),
  setup_fee_paid: z.boolean().optional(),
  setup_fee_paid_amount: z.string().nullable().optional(),
  /** The GST frozen on the charge, and its tax-invoice number once paid. */
  tax_rate: z.string().optional(),
  tax_amount: z.string().optional(),
  invoice_number: z.string().nullable().optional(),
});

export type SubscriptionInvoice = z.infer<typeof subscriptionInvoiceSchema>;

export function fetchMerchantInvoices(
  tenantId: string,
  params: { page?: number; status?: SubscriptionInvoiceStatus | '' } = {},
) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.status) search.set('status', params.status);
  const query = search.toString();
  return apiRequest(
    `/platform/merchants/${tenantId}/invoices${query ? `?${query}` : ''}`,
    pageSchema(subscriptionInvoiceSchema),
  );
}

/** Write off a charge the merchant is never going to complete. */
export function voidMerchantInvoice(tenantId: string, invoiceId: string, reason: string) {
  return apiRequest(
    `/platform/merchants/${tenantId}/invoices/${invoiceId}/void`,
    subscriptionInvoiceSchema,
    { method: 'POST', body: { reason } },
  );
}

/** Record money that arrived by bank transfer, where no webhook is coming. */
export function markMerchantInvoicePaid(
  tenantId: string,
  invoiceId: string,
  reference: string,
) {
  return apiRequest(
    `/platform/merchants/${tenantId}/invoices/${invoiceId}/mark-paid`,
    subscriptionInvoiceSchema,
    { method: 'POST', body: { reference } },
  );
}

// --- Audit -----------------------------------------------------------------

export const auditEntrySchema = z.object({
  id: z.string(),
  tenant_id: z.string().nullable(),
  user_id: z.string().nullable(),
  action: z.string(),
  entity_type: z.string().nullable(),
  entity_id: z.string().nullable(),
  old_value: z.record(z.unknown()).nullable(),
  new_value: z.record(z.unknown()).nullable(),
  ip_address: z.string().nullable(),
  created_at: z.string(),
  actor_name: z.string().nullable(),
  actor_email: z.string().nullable(),
  tenant_name: z.string().nullable(),
});

export type AuditEntry = z.infer<typeof auditEntrySchema>;

export interface AuditListParams {
  page?: number;
  tenant_id?: string;
  user_id?: string;
  action?: string;
}

export function fetchAuditEntries(params: AuditListParams = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.tenant_id) search.set('tenant_id', params.tenant_id);
  if (params.user_id) search.set('user_id', params.user_id);
  if (params.action) search.set('action', params.action);
  const query = search.toString();
  return apiRequest(
    `/platform/audit${query ? `?${query}` : ''}`,
    pageSchema(auditEntrySchema),
  );
}

export function fetchAuditActions() {
  return apiRequest('/platform/audit/actions', z.array(z.string()));
}

// --- Scheduled jobs --------------------------------------------------------

export const jobHealthSchema = z.object({
  name: z.string(),
  description: z.string(),
  /** The cadence in force: the owner's override when there is one. */
  interval_seconds: z.number(),
  /** The cadence declared in code, which "reset" goes back to. */
  default_interval_seconds: z.number(),
  interval_overridden: z.boolean().default(false),
  interval_note: z.string().nullable().default(null),
  interval_updated_at: z.string().nullable().default(null),
  last_status: z.string().nullable(),
  last_started_at: z.string().nullable(),
  last_finished_at: z.string().nullable(),
  last_detail: z.string().nullable(),
  last_processed: z.number(),
  last_success_at: z.string().nullable(),
  /** Computed from the declared cadence. True includes "has never run". */
  overdue: z.boolean(),
});

export const jobRunSchema = z.object({
  id: z.string(),
  job: z.string(),
  status: z.string(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  processed: z.number(),
  /** The one-line summary. */
  detail: z.string().nullable(),
  /** The full traceback of a FAILED run; null for every other outcome. */
  error: z.string().nullable().default(null),
  /** Finished minus started. Null while the run is still going. */
  duration_ms: z.number().nullable().default(null),
});

export type JobHealth = z.infer<typeof jobHealthSchema>;
export type JobRun = z.infer<typeof jobRunSchema>;

export function fetchJobHealth() {
  return apiRequest('/platform/jobs', z.array(jobHealthSchema));
}

export function fetchJobRuns(job?: string) {
  return apiRequest(
    `/platform/jobs/runs${job ? `?job=${encodeURIComponent(job)}` : ''}`,
    z.array(jobRunSchema),
  );
}

export function runJobNow(job: string) {
  return apiRequest(`/platform/jobs/${job}/run`, jobRunSchema, { method: 'POST' });
}

/** The range the server accepts, in seconds: once a minute to once a week. */
export const JOB_INTERVAL_MIN_SECONDS = 60;
export const JOB_INTERVAL_MAX_SECONDS = 7 * 86_400;

export function setJobSchedule(job: string, body: { interval_seconds: number; note?: string }) {
  return apiRequest(`/platform/jobs/${job}/schedule`, jobHealthSchema, {
    method: 'PUT',
    body,
  });
}

export function resetJobSchedule(job: string) {
  return apiRequest(`/platform/jobs/${job}/schedule`, jobHealthSchema, { method: 'DELETE' });
}

// --- Comms -----------------------------------------------------------------

export const ANNOUNCEMENT_LEVELS = ['INFO', 'WARNING', 'CRITICAL'] as const;
export const ANNOUNCEMENT_AUDIENCES = ['DASHBOARD', 'STOREFRONT', 'BOTH'] as const;

export type AnnouncementLevel = (typeof ANNOUNCEMENT_LEVELS)[number];
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];

export const announcementSchema = z.object({
  id: z.string(),
  message: z.string(),
  level: z.enum(ANNOUNCEMENT_LEVELS),
  audience: z.enum(ANNOUNCEMENT_AUDIENCES),
  starts_at: z.string(),
  ends_at: z.string().nullable(),
  active: z.boolean(),
  created_at: z.string(),
});

export const emailTemplateSchema = z.object({
  key: z.string(),
  description: z.string(),
  subject: z.string(),
  body: z.string(),
  enabled: z.boolean(),
  /** True when a row overrides the shipped default - drives "reset". */
  customised: z.boolean(),
  variables: z.record(z.string()),
  /** Placeholders this template may not be saved without - e.g. its link. */
  required_variables: z.array(z.string()).default([]),
  /** True when the platform cannot function without sending it: no off switch. */
  essential: z.boolean().default(false),
  default_subject: z.string(),
  default_body: z.string(),
  preview_subject: z.string(),
  preview_body: z.string(),
});

export type Announcement = z.infer<typeof announcementSchema>;
export type EmailTemplate = z.infer<typeof emailTemplateSchema>;

// --- Push updates to merchants ------------------------------------------------
export const pushBroadcastSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  url: z.string().nullable(),
  plans: z.array(z.string()).nullable(),
  merchants: z.number(),
  devices: z.number(),
  delivered: z.number(),
  created_at: z.string(),
});
export type PushBroadcast = z.infer<typeof pushBroadcastSchema>;

export function fetchPushBroadcasts() {
  return apiRequest('/platform/push-broadcasts', z.array(pushBroadcastSchema));
}

export function sendPushBroadcast(payload: {
  title: string;
  body: string;
  path?: string;
  plans?: string[];
}) {
  return apiRequest('/platform/push-broadcasts', pushBroadcastSchema, {
    method: 'POST',
    body: payload,
  });
}

export function fetchAnnouncements() {
  return apiRequest('/platform/announcements', z.array(announcementSchema));
}

export function createAnnouncement(payload: {
  message: string;
  level: AnnouncementLevel;
  audience: AnnouncementAudience;
  starts_at?: string;
  ends_at?: string | null;
}) {
  return apiRequest('/platform/announcements', announcementSchema, {
    method: 'POST',
    body: payload,
  });
}

export function takeDownAnnouncement(id: string) {
  return apiRequest(`/platform/announcements/${id}`, announcementSchema, { method: 'DELETE' });
}

// --- Maintenance windows ---------------------------------------------------

export const MAINTENANCE_MODES = ['READ_ONLY', 'FULL'] as const;
export const MAINTENANCE_STATUSES = ['SCHEDULED', 'ACTIVE', 'COMPLETED', 'CANCELLED'] as const;

export type MaintenanceWindowMode = (typeof MAINTENANCE_MODES)[number];
export type MaintenanceWindowStatus = (typeof MAINTENANCE_STATUSES)[number];

/** Mirrors `MaintenanceWindowRead` in `app/api/v1/platform/maintenance.py`. */
export const maintenanceWindowSchema = z.object({
  id: z.string(),
  mode: z.enum(MAINTENANCE_MODES),
  status: z.enum(MAINTENANCE_STATUSES),
  message: z.string().nullable(),
  starts_at: z.string(),
  ends_at: z.string().nullable(),
  started_at: z.string().nullable(),
  ended_at: z.string().nullable(),
  notified_at: z.string().nullable(),
  created_at: z.string(),
});

export type MaintenanceWindow = z.infer<typeof maintenanceWindowSchema>;

export function fetchMaintenanceWindows() {
  return apiRequest('/platform/maintenance', z.array(maintenanceWindowSchema));
}

export function createMaintenanceWindow(payload: {
  mode: MaintenanceWindowMode;
  message: string;
  starts_at: string;
  /** Required: the expected end everyone is shown. Advisory - only `/end` ends it. */
  ends_at: string;
  notify_merchants: boolean;
}) {
  return apiRequest('/platform/maintenance', maintenanceWindowSchema, {
    method: 'POST',
    body: payload,
  });
}

/** Start now, end now, or cancel one that has not started. */
export function changeMaintenanceWindow(id: string, action: 'start' | 'end' | 'cancel') {
  return apiRequest(`/platform/maintenance/${id}/${action}`, maintenanceWindowSchema, {
    method: 'POST',
  });
}

export function fetchEmailTemplates() {
  return apiRequest('/platform/email-templates', z.array(emailTemplateSchema));
}

export function saveEmailTemplate(
  key: string,
  payload: { subject: string; body: string; enabled: boolean },
) {
  return apiRequest(`/platform/email-templates/${key}`, emailTemplateSchema, {
    method: 'PUT',
    body: payload,
  });
}

export function resetEmailTemplate(key: string) {
  return apiRequest(`/platform/email-templates/${key}`, emailTemplateSchema, {
    method: 'DELETE',
  });
}

// --- Integrations ----------------------------------------------------------

export const integrationHealthSchema = z.object({
  key: z.string(),
  name: z.string(),
  /** PLATFORM = our account, from settings. MERCHANT = each shop connects it. */
  scope: z.enum(['PLATFORM', 'MERCHANT']),
  /** False means it is running on a local stub rather than real credentials. */
  configured: z.boolean(),
  detail: z.string(),
  enabled: z.boolean(),
  flag: z.string().nullable(),
  connected_merchants: z.number().nullable(),
});

export const integrationUsageSchema = z.object({
  tenant_id: z.string(),
  tenant_name: z.string(),
  ai_calls: z.number(),
  ai_input_tokens: z.number(),
  ai_output_tokens: z.number(),
  /** Estimated from metered tokens at the current rate card. */
  ai_cost_inr: z.string(),
  /** False when a model on this row is missing from the rate card. */
  ai_cost_complete: z.boolean(),
  whatsapp_sent: z.number(),
  shipments_booked: z.number(),
});

export const integrationTotalsSchema = z.object({
  window_days: z.number(),
  merchants_using_ai: z.number(),
  ai_calls: z.number(),
  ai_cost_inr: z.string(),
  whatsapp_sent: z.number(),
  shipments_booked: z.number(),
  cost_complete: z.boolean(),
});

export type IntegrationHealth = z.infer<typeof integrationHealthSchema>;
export type IntegrationUsage = z.infer<typeof integrationUsageSchema>;
export type IntegrationTotals = z.infer<typeof integrationTotalsSchema>;

export function fetchIntegrationHealth() {
  return apiRequest('/platform/integrations/health', z.array(integrationHealthSchema));
}

export function fetchIntegrationUsage(params: { page?: number; days?: number; q?: string } = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.days) search.set('days', String(params.days));
  if (params.q) search.set('q', params.q);
  const query = search.toString();
  return apiRequest(
    `/platform/integrations/usage${query ? `?${query}` : ''}`,
    pageSchema(integrationUsageSchema),
  );
}

export function fetchIntegrationTotals(days = 30) {
  return apiRequest(
    `/platform/integrations/usage/totals?days=${days}`,
    integrationTotalsSchema,
  );
}

// --- AI expenses -----------------------------------------------------------

/**
 * What AI costs the platform. Every money figure is a decimal string in INR,
 * estimated from metered tokens and image counts at the current rate card.
 */

export const AI_EXPENSE_RANGES = ['7d', '30d', '90d', '12m', 'mtd', 'ytd', 'all'] as const;

const aiExpenseBreakdownSchema = z.object({
  key: z.string(),
  label: z.string(),
  calls: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  images: z.number(),
  cost_inr: z.string(),
  share_pct: z.number(),
  cost_complete: z.boolean(),
});

const aiExpenseModelSchema = z.object({
  model: z.string(),
  vendor: z.string(),
  vendor_label: z.string(),
  categories: z.array(z.string()),
  calls: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  images: z.number(),
  cost_inr: z.string(),
  share_pct: z.number(),
  /** False when the model is missing from the rate card it needs. */
  priced: z.boolean(),
  input_usd_per_mtok: z.string().nullable(),
  output_usd_per_mtok: z.string().nullable(),
  image_usd: z.string().nullable(),
  image_hd_usd: z.string().nullable(),
});

export const aiExpenseMerchantSchema = z.object({
  tenant_id: z.string(),
  tenant_name: z.string(),
  tenant_slug: z.string().nullable(),
  calls: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  images: z.number(),
  cost_inr: z.string(),
  share_pct: z.number(),
  cost_complete: z.boolean(),
  top_model: z.string().nullable(),
  top_category: z.string().nullable(),
  last_used_at: z.string().nullable(),
});

const aiExpenseOptionSchema = z.object({ key: z.string(), label: z.string() });

export const aiExpenseReportSchema = z.object({
  window: z.object({
    range: z.string(),
    start: z.string(),
    end: z.string(),
    previous_start: z.string(),
    previous_end: z.string(),
    granularity: z.string(),
  }),
  merchant: z
    .object({ id: z.string(), name: z.string(), slug: z.string().nullable(), status: z.string() })
    .nullable(),
  summary: z.object({
    cost_inr: z.string(),
    cost_usd: z.string(),
    previous_cost_inr: z.string(),
    change_pct: z.number().nullable(),
    calls: z.number(),
    previous_calls: z.number(),
    input_tokens: z.number(),
    output_tokens: z.number(),
    images: z.number(),
    merchants: z.number(),
    avg_cost_per_call_inr: z.string(),
    avg_cost_per_merchant_inr: z.string(),
    credit_sales_inr: z.string(),
    projected_30d_cost_inr: z.string().nullable(),
    cost_complete: z.boolean(),
    unpriced_models: z.array(z.string()),
    usd_to_inr: z.string(),
  }),
  trend: z.array(
    z.object({
      bucket: z.string(),
      label: z.string(),
      cost_inr: z.string(),
      calls: z.number(),
      tokens: z.number(),
      images: z.number(),
      by_category: z.record(z.string()),
    }),
  ),
  by_category: z.array(aiExpenseBreakdownSchema),
  by_vendor: z.array(aiExpenseBreakdownSchema),
  by_feature: z.array(aiExpenseBreakdownSchema),
  by_model: z.array(aiExpenseModelSchema),
  top_merchants: z.array(aiExpenseMerchantSchema),
  options: z.object({
    vendors: z.array(aiExpenseOptionSchema),
    categories: z.array(aiExpenseOptionSchema),
    models: z.array(aiExpenseOptionSchema),
    features: z.array(aiExpenseOptionSchema),
  }),
});

export type AIExpenseReport = z.infer<typeof aiExpenseReportSchema>;
export type AIExpenseBreakdown = z.infer<typeof aiExpenseBreakdownSchema>;
export type AIExpenseModel = z.infer<typeof aiExpenseModelSchema>;
export type AIExpenseMerchant = z.infer<typeof aiExpenseMerchantSchema>;

export interface AIExpenseFilters {
  range?: string;
  date_from?: string;
  date_to?: string;
  tenant_id?: string;
  vendor?: string;
  category?: string;
  model?: string;
  feature?: string;
}

function aiExpenseQuery(filters: AIExpenseFilters & { page?: number; q?: string }) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export function fetchAIExpenses(filters: AIExpenseFilters = {}) {
  return apiRequest(`/platform/ai-expenses${aiExpenseQuery(filters)}`, aiExpenseReportSchema);
}

export function fetchAIExpenseMerchants(
  filters: Omit<AIExpenseFilters, 'tenant_id'> & { page?: number; q?: string } = {},
) {
  return apiRequest(
    `/platform/ai-expenses/merchants${aiExpenseQuery(filters)}`,
    pageSchema(aiExpenseMerchantSchema),
  );
}

// --- Shipping --------------------------------------------------------------

export const platformShippingProviderSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  capabilities: z.array(z.string()),
  /** Off means no merchant can quote or book through it, whatever they saved. */
  enabled: z.boolean(),
  note: z.string().nullable(),
  connected_merchants: z.number(),
  merchants_with_errors: z.number(),
  updated_at: z.string().nullable(),
});

export const platformShippingOverviewSchema = z.object({
  window_days: z.number(),
  merchants_enabled: z.number(),
  merchants_connected: z.number(),
  merchants_with_credential_errors: z.number(),
  shipments_booked: z.number(),
  shipments_failed: z.number(),
  shipments_active: z.number(),
  shipments_delivered: z.number(),
  shipments_rto: z.number(),
  webhooks_received: z.number(),
  webhooks_failed: z.number(),
  /** The SHIPPING_INTEGRATION switch, so the page can say when it is off. */
  integration_enabled: z.boolean(),
});

export const PLATFORM_SHIPPING_ERROR_KINDS = ['SHIPMENT', 'CREDENTIAL', 'WEBHOOK'] as const;

export const platformShippingErrorSchema = z.object({
  kind: z.enum(PLATFORM_SHIPPING_ERROR_KINDS),
  id: z.string(),
  tenant_id: z.string().nullable(),
  tenant_name: z.string().nullable(),
  provider: z.string(),
  /** The order number, AWB or webhook event the error is about. */
  reference: z.string().nullable(),
  error: z.string(),
  occurred_at: z.string(),
});

export type PlatformShippingProvider = z.infer<typeof platformShippingProviderSchema>;
export type PlatformShippingOverview = z.infer<typeof platformShippingOverviewSchema>;
export type PlatformShippingError = z.infer<typeof platformShippingErrorSchema>;

export function fetchPlatformShippingProviders() {
  return apiRequest('/platform/shipping/providers', z.array(platformShippingProviderSchema));
}

/** A reason is mandatory: this switches a courier off for every shop at once. */
export function setPlatformShippingProvider(
  key: string,
  payload: { enabled: boolean; note?: string; reason: string },
) {
  return apiRequest(`/platform/shipping/providers/${key}`, platformShippingProviderSchema, {
    method: 'PATCH',
    body: payload,
  });
}

export function fetchPlatformShippingOverview(days = 7) {
  return apiRequest(`/platform/shipping/overview?days=${days}`, platformShippingOverviewSchema);
}

export function fetchPlatformShippingErrors(limit = 50) {
  return apiRequest(
    `/platform/shipping/errors?limit=${limit}`,
    z.array(platformShippingErrorSchema),
  );
}

// --- Feature flags ---------------------------------------------------------

export const featureFlagSchema = z.object({
  key: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  /** What a blocked user is told. Falls back to the flag's declared default. */
  message: z.string(),
  note: z.string().nullable(),
  override_count: z.number(),
  /** Shown to merchants on the paused screen while it is off. */
  expected_back_at: z.string().nullish(),
  /** When the scheduler turns it back on by itself. */
  auto_enable_at: z.string().nullish(),
});

export const flagOverrideSchema = z.object({
  key: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string().nullable(),
  enabled: z.boolean(),
  note: z.string().nullable(),
  created_at: z.string(),
});

export type FeatureFlag = z.infer<typeof featureFlagSchema>;
export type FlagOverride = z.infer<typeof flagOverrideSchema>;

export function fetchFeatureFlags() {
  return apiRequest('/platform/flags', z.array(featureFlagSchema));
}

export function setFeatureFlag(
  key: string,
  payload: {
    enabled: boolean;
    message?: string;
    note?: string;
    expected_back_at?: string;
    auto_enable_at?: string;
  },
) {
  return apiRequest(`/platform/flags/${key}`, featureFlagSchema, {
    method: 'PUT',
    body: payload,
  });
}

export function fetchFlagOverrides(key: string) {
  return apiRequest(`/platform/flags/${key}/overrides`, z.array(flagOverrideSchema));
}

export function setFlagOverride(
  key: string,
  tenantId: string,
  payload: { enabled: boolean; note?: string },
) {
  return apiRequest(
    `/platform/flags/${key}/overrides/${tenantId}`,
    z.object({ key: z.string(), enabled: z.boolean() }),
    { method: 'PUT', body: payload },
  );
}

export function clearFlagOverride(key: string, tenantId: string) {
  return apiRequest(
    `/platform/flags/${key}/overrides/${tenantId}`,
    z.object({ key: z.string(), cleared: z.boolean() }),
    { method: 'DELETE' },
  );
}

// --- Custom domains --------------------------------------------------------

export const PLATFORM_DOMAIN_STATUSES = ['PENDING', 'VERIFIED', 'FAILED', 'RELEASED'] as const;
export type PlatformDomainStatus = (typeof PLATFORM_DOMAIN_STATUSES)[number];

export const PLATFORM_CERTIFICATE_STATUSES = [
  'NOT_REQUESTED',
  'PENDING',
  'ISSUED',
  'FAILED',
] as const;
export type PlatformCertificateStatus = (typeof PLATFORM_CERTIFICATE_STATUSES)[number];

export const platformDomainSchema = z.object({
  id: z.string(),
  domain: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string(),
  store_id: z.string(),
  status: z.enum(PLATFORM_DOMAIN_STATUSES),
  certificate_status: z.enum(PLATFORM_CERTIFICATE_STATUSES),
  certificate_detail: z.string().nullable(),
  cname_ok: z.boolean(),
  last_error: z.string().nullable(),
  check_attempts: z.number(),
  last_checked_at: z.string().nullable(),
  verified_at: z.string().nullable(),
  created_at: z.string(),
});

export const platformDomainDetailSchema = platformDomainSchema.extend({
  token: z.string(),
  requested_by_user_id: z.string().nullable(),
  // Rival claims on the same hostname. A dispute belongs to the domain, not to
  // one row, so staff see both sides at once.
  competing_claims: z.array(platformDomainSchema),
});

export const domainCountsSchema = z.object({
  total: z.number(),
  pending: z.number(),
  failed: z.number(),
  awaiting_certificate: z.number(),
});

export type PlatformDomain = z.infer<typeof platformDomainSchema>;
export type PlatformDomainDetail = z.infer<typeof platformDomainDetailSchema>;
export type DomainCounts = z.infer<typeof domainCountsSchema>;

export interface DomainListParams {
  page?: number;
  status?: PlatformDomainStatus;
  certificate_status?: PlatformCertificateStatus;
  tenant_id?: string;
  q?: string;
}

export function fetchPlatformDomains(params: DomainListParams = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.status) search.set('status', params.status);
  if (params.certificate_status) search.set('certificate_status', params.certificate_status);
  if (params.tenant_id) search.set('tenant_id', params.tenant_id);
  if (params.q) search.set('q', params.q);
  const query = search.toString();
  return apiRequest(
    `/platform/domains${query ? `?${query}` : ''}`,
    pageSchema(platformDomainSchema),
  );
}

export function fetchPlatformDomain(domainId: string) {
  return apiRequest(`/platform/domains/${domainId}`, platformDomainDetailSchema);
}

export function fetchDomainCounts() {
  return apiRequest('/platform/domains/counts', domainCountsSchema);
}

/**
 * The prepaid float every purchased domain is actually paid for out of.
 *
 * Nothing in the platform tops it up - it is added in the registrar's own
 * panel - and running it dry turns each purchase into a charge followed by a
 * refund. `available` being null means it could not be read, which is not the
 * same as empty and must not be drawn as zero.
 */
export const registrarBalanceSchema = z.object({
  available: z.string().nullable().optional().default(null),
  currency: z.string().optional().default('INR'),
  minimum: z.string().optional().default('0'),
  low: z.boolean().optional().default(false),
});

export type RegistrarBalance = z.infer<typeof registrarBalanceSchema>;

export function fetchRegistrarBalance() {
  return apiRequest('/platform/domains/registrar-balance', registrarBalanceSchema);
}

/** Binds a domain without the DNS proof. Reason is recorded permanently. */
export function forceVerifyDomain(domainId: string, reason: string) {
  return apiRequest(`/platform/domains/${domainId}/force-verify`, platformDomainDetailSchema, {
    method: 'POST',
    body: { reason },
  });
}

/** Frees a hostname so its rightful owner can claim and prove it themselves. */
export function releaseDomain(domainId: string, reason: string) {
  return apiRequest(`/platform/domains/${domainId}/release`, platformDomainDetailSchema, {
    method: 'POST',
    body: { reason },
  });
}

export function markCertificateIssued(domainId: string) {
  return apiRequest(
    `/platform/domains/${domainId}/certificate-issued`,
    platformDomainDetailSchema,
    { method: 'POST' },
  );
}

// --- Webhook delivery ------------------------------------------------------

export const WEBHOOK_STATES = ['OK', 'FAILED', 'PENDING'] as const;
export type WebhookState = (typeof WEBHOOK_STATES)[number];

export const webhookEventSchema = z.object({
  id: z.string(),
  provider: z.string(),
  /** The provider's own id, which is what a gateway dashboard shows. */
  event_id: z.string(),
  event_type: z.string().nullable(),
  state: z.enum(WEBHOOK_STATES),
  error: z.string().nullable(),
  attempts: z.number(),
  tenant_id: z.string().nullable(),
  tenant_name: z.string().nullable(),
  processed_at: z.string().nullable(),
  last_attempt_at: z.string().nullable(),
  created_at: z.string(),
  // Decided server-side. The rule behind it protects against a double refund,
  // so the client renders the answer rather than re-deriving it.
  replayable: z.boolean(),
  replay_blocked_reason: z.string().nullable(),
});

export const webhookEventDetailSchema = webhookEventSchema.extend({
  payload: z.record(z.unknown()),
});

export const webhookStatsSchema = z.object({
  window_hours: z.number(),
  total: z.number(),
  failed: z.number(),
  pending: z.number(),
  merchants_affected: z.number(),
});

export type WebhookEvent = z.infer<typeof webhookEventSchema>;
export type WebhookEventDetail = z.infer<typeof webhookEventDetailSchema>;
export type WebhookStats = z.infer<typeof webhookStatsSchema>;

export interface WebhookListParams {
  page?: number;
  state?: WebhookState;
  tenant_id?: string;
  event_type?: string;
  q?: string;
}

export function fetchWebhookEvents(params: WebhookListParams = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.state) search.set('state', params.state);
  if (params.tenant_id) search.set('tenant_id', params.tenant_id);
  if (params.event_type) search.set('event_type', params.event_type);
  if (params.q) search.set('q', params.q);
  const query = search.toString();
  return apiRequest(
    `/platform/webhooks${query ? `?${query}` : ''}`,
    pageSchema(webhookEventSchema),
  );
}

export function fetchWebhookEvent(eventId: string) {
  return apiRequest(`/platform/webhooks/${eventId}`, webhookEventDetailSchema);
}

export function fetchWebhookStats(windowHours = 24) {
  return apiRequest(`/platform/webhooks/stats?window_hours=${windowHours}`, webhookStatsSchema);
}

export function fetchWebhookEventTypes() {
  return apiRequest('/platform/webhooks/event-types', z.array(z.string()));
}

/** Re-run a stuck event. Refused by the API for one that already applied. */
export function replayWebhookEvent(eventId: string, reason: string) {
  return apiRequest(`/platform/webhooks/${eventId}/replay`, webhookEventDetailSchema, {
    method: 'POST',
    body: { reason },
  });
}

// --- Staff -----------------------------------------------------------------

export const platformStaffSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  email: z.string(),
  role: z.string(),
  status: z.string(),
  last_login_at: z.string().nullable(),
  created_at: z.string(),
  /**
   * What this colleague may actually do, already resolved server-side from
   * their role and any override. Read rather than recomputed, so the screen can
   * never disagree with the API about somebody's access.
   */
  permissions: z.array(z.string()),
  /** True when the grants above were ticked by hand rather than left to the role. */
  has_custom_permissions: z.boolean(),
});

export type PlatformStaff = z.infer<typeof platformStaffSchema>;

export const platformStaffSummarySchema = z.object({
  total: z.number(),
  active: z.number(),
  invited: z.number(),
  disabled: z.number(),
  super_admins: z.number(),
  billing: z.number(),
  support: z.number(),
  never_signed_in: z.number(),
});

export type PlatformStaffSummary = z.infer<typeof platformStaffSummarySchema>;

/** Every role a platform account can hold, widest reach first. */
export const PLATFORM_STAFF_ROLES = ['SUPER_ADMIN', 'PLATFORM_BILLING', 'PLATFORM_SUPPORT'] as const;

export type PlatformStaffRole = (typeof PLATFORM_STAFF_ROLES)[number];

export const STAFF_ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super admin',
  PLATFORM_BILLING: 'Billing',
  PLATFORM_SUPPORT: 'Support',
};

/**
 * What each role actually lets somebody do, in the words a reader deciding
 * between them needs - not a restatement of the role's name.
 */
export const STAFF_ROLE_BLURBS: Record<string, string> = {
  SUPER_ADMIN: 'Everything, including managing staff and other super admins.',
  PLATFORM_BILLING: 'Plans, prices and who is on which. Can suspend accounts.',
  PLATFORM_SUPPORT: 'Read-only. Can look at any merchant to answer a ticket.',
};

/** A role code in the words the screen uses, falling back to the code itself. */
export function staffRoleLabel(role: string): string {
  return STAFF_ROLE_LABELS[role] ?? role.replace(/_/g, ' ').toLowerCase();
}

export const STAFF_STATUSES = ['ACTIVE', 'INVITED', 'DISABLED'] as const;

export type StaffStatus = (typeof STAFF_STATUSES)[number];

export const STAFF_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  INVITED: 'Invitation open',
  DISABLED: 'Disabled',
};

export const STAFF_SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'last_seen', label: 'Recently signed in' },
] as const;

export type StaffSort = (typeof STAFF_SORTS)[number]['value'];

export interface StaffListParams {
  page?: number;
  q?: string;
  role?: PlatformStaffRole | '';
  status?: StaffStatus | '';
  sort?: StaffSort;
}

export function fetchStaff(params: StaffListParams = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.q) search.set('q', params.q);
  if (params.role) search.set('role', params.role);
  if (params.status) search.set('status', params.status);
  if (params.sort) search.set('sort', params.sort);
  const query = search.toString();
  return apiRequest(
    `/platform/staff${query ? `?${query}` : ''}`,
    pageSchema(platformStaffSchema),
  );
}

export function fetchStaffSummary() {
  return apiRequest('/platform/staff/summary', platformStaffSummarySchema);
}

export function fetchAssignableRoles() {
  return apiRequest('/platform/staff/roles', z.array(z.string()));
}

/**
 * Opening a colleague's account.
 *
 * Sending `password` skips the invitation email entirely: the account is
 * created ACTIVE with that credential, for staff who are handing it over out of
 * band. Omitting it is the safer default and the one the dialog starts on.
 */
export function inviteStaff(payload: {
  email: string;
  full_name: string;
  role: string;
  password?: string;
}) {
  return apiRequest('/platform/staff', platformStaffSchema, { method: 'POST', body: payload });
}

/**
 * The platform surface as the permissions editor renders it.
 *
 * Same envelope the merchant team screen fetches from `/users/permissions`,
 * carrying a different list of modules - which is what lets one editor render
 * either.
 */
export function fetchStaffPermissionCatalogue() {
  return apiRequest('/platform/staff/permissions', permissionCatalogueSchema);
}

/**
 * Pin a colleague's grants, or hand them back to their role.
 *
 * `null` and `[]` are different answers and both are meant: null clears the
 * override so the role decides again, while an empty list pins them to holding
 * nothing at all.
 */
export function setStaffPermissions(staffId: string, permissions: string[] | null) {
  return apiRequest(`/platform/staff/${staffId}/permissions`, platformStaffSchema, {
    method: 'PUT',
    body: { permissions },
  });
}

export function changeStaffRole(staffId: string, role: string) {
  return apiRequest(`/platform/staff/${staffId}/role`, platformStaffSchema, {
    method: 'PUT',
    body: { role },
  });
}

export function disableStaff(staffId: string) {
  return apiRequest(`/platform/staff/${staffId}`, platformStaffSchema, { method: 'DELETE' });
}

// --- Overview --------------------------------------------------------------

export const overviewSchema = z.object({
  total_merchants: z.number(),
  active_merchants: z.number(),
  suspended_merchants: z.number(),
  merchants_created_this_month: z.number(),
  total_orders: z.number(),
  orders_this_month: z.number(),
  gross_revenue: z.string(),
  gross_revenue_this_month: z.string(),
  mrr_minor: z.number(),
  currency: z.string(),
  plan_mix: z.array(
    z.object({
      plan_code: z.string(),
      plan_name: z.string(),
      merchants: z.number(),
      mrr_minor: z.number(),
    }),
  ),
  onboarding_funnel: z.array(z.object({ step: z.string(), merchants: z.number() })),
  /**
   * Collected plan money, as against contracted `mrr_minor`. The backend has
   * always sent these three; the schema used to drop them on the floor.
   */
  subscription_revenue: z.string().optional().default('0'),
  subscription_revenue_this_month: z.string().optional().default('0'),
  archived_merchants: z.number().optional().default(0),
  /**
   * Domain resale - the one line here with a cost against it. The margin is
   * the platform's own markup over what its registrar account was billed, so
   * it understates the margin down the whole chain.
   */
  domain_revenue: z.string().optional().default('0'),
  domain_revenue_this_month: z.string().optional().default('0'),
  domain_margin: z.string().optional().default('0'),
  domain_margin_this_month: z.string().optional().default('0'),
  /**
   * Paid domain charges written before the cost was recorded: revenue with no
   * margin against it. Reported so a thin margin reads as incomplete rather
   * than alarming.
   */
  domain_revenue_unpriced: z.string().optional().default('0'),
});

export type PlatformOverview = z.infer<typeof overviewSchema>;

export function fetchOverview() {
  return apiRequest('/platform/overview', overviewSchema);
}

/** Paise to rupees, as a decimal string the money formatter understands. */
export function minorToDecimalString(minor: number): string {
  return (minor / 100).toFixed(2);
}

// --- Cross-tenant orders ---------------------------------------------------

export const platformOrderSchema = z.object({
  id: z.string(),
  order_number: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  status: z.string(),
  payment_status: z.string(),
  total: z.string(),
  created_at: z.string(),
});

export const platformOrderDetailSchema = platformOrderSchema.extend({
  payment_method: z.string(),
  subtotal: z.string(),
  discount_amount: z.string(),
  tax_amount: z.string(),
  shipping_amount: z.string(),
  discount_code: z.string().nullable(),
  shipping_address: z.record(z.unknown()),
  customer_note: z.string().nullable(),
});

export type PlatformOrder = z.infer<typeof platformOrderSchema>;
export type PlatformOrderDetail = z.infer<typeof platformOrderDetailSchema>;

export function fetchPlatformOrders(params: { page?: number; q?: string; tenant_id?: string } = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.q) search.set('q', params.q);
  if (params.tenant_id) search.set('tenant_id', params.tenant_id);
  const query = search.toString();
  return apiRequest(
    `/platform/orders${query ? `?${query}` : ''}`,
    pageSchema(platformOrderSchema),
  );
}

export function fetchPlatformOrder(orderId: string) {
  return apiRequest(`/platform/orders/${orderId}`, platformOrderDetailSchema);
}

// --- Product moderation ----------------------------------------------------

export const platformProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string(),
  tenant_slug: z.string(),
  status: z.string(),
  price: z.string(),
  created_at: z.string(),
});

export type PlatformProduct = z.infer<typeof platformProductSchema>;

export function fetchPlatformProducts(
  params: { page?: number; q?: string; status?: string; tenant_id?: string } = {},
) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.q) search.set('q', params.q);
  if (params.status) search.set('status', params.status);
  if (params.tenant_id) search.set('tenant_id', params.tenant_id);
  const query = search.toString();
  return apiRequest(
    `/platform/products${query ? `?${query}` : ''}`,
    pageSchema(platformProductSchema),
  );
}

export function takeDownProduct(productId: string, reason: string) {
  return apiRequest(`/platform/products/${productId}/takedown`, platformProductSchema, {
    method: 'POST',
    body: { reason },
  });
}

// --- Cross-tenant users ----------------------------------------------------

export const platformUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string(),
  phone: z.string().nullable(),
  role: z.string(),
  status: z.string(),
  tenant_id: z.string().nullable(),
  tenant_name: z.string().nullable(),
  email_verified: z.boolean(),
  last_login_at: z.string().nullable(),
  created_at: z.string(),
});

export type PlatformUserRecord = z.infer<typeof platformUserSchema>;

export function fetchPlatformUsers(params: { page?: number; q?: string; tenant_id?: string } = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.q) search.set('q', params.q);
  if (params.tenant_id) search.set('tenant_id', params.tenant_id);
  const query = search.toString();
  return apiRequest(`/platform/users${query ? `?${query}` : ''}`, pageSchema(platformUserSchema));
}

export function sendUserPasswordReset(userId: string) {
  return apiRequest(`/platform/users/${userId}/password-reset`, platformUserSchema, {
    method: 'POST',
  });
}

/**
 * Platform owner only: replace a merchant user's password. Neither the old
 * password nor the new one comes back - the response is the account summary.
 */
export function setUserPassword(
  userId: string,
  payload: { password: string; require_change: boolean },
) {
  return apiRequest(`/platform/users/${userId}/password`, platformUserSchema, {
    method: 'PUT',
    body: payload,
  });
}

export function setUserDisabled(userId: string, disabled: boolean) {
  return apiRequest(`/platform/users/${userId}/disabled`, platformUserSchema, {
    method: 'PUT',
    body: { disabled },
  });
}

// --- Partners & referrals --------------------------------------------------

/**
 * The affiliate and partner programme, seen from the platform side.
 *
 * A partner introduces merchants with a referral code, and a code carries two
 * things that are configured *independently*: a discount for the merchant who
 * redeems it, and a commission for the partner who owns it. Either can be
 * absent - a code with neither is still a valid code that attributes signups -
 * so every field of both blocks is nullable and neither is ever inferred from
 * the other.
 *
 * What a partner is allowed to see of all this is in `features/partners`; this
 * module is the staff view, which includes internal notes and other partners'
 * numbers and must never be reachable from a partner's own session.
 */

export const PARTNER_TYPES = ['SALES_REP', 'INFLUENCER', 'AFFILIATE', 'CHANNEL_PARTNER'] as const;
export type PartnerType = (typeof PARTNER_TYPES)[number];

export const PARTNER_TYPE_LABELS: Record<PartnerType, string> = {
  SALES_REP: 'Sales rep',
  INFLUENCER: 'Influencer',
  AFFILIATE: 'Affiliate',
  CHANNEL_PARTNER: 'Channel partner',
};

export const PARTNER_STATUSES = ['PENDING', 'ACTIVE', 'SUSPENDED', 'REJECTED'] as const;
export type PartnerStatus = (typeof PARTNER_STATUSES)[number];

export const REFERRAL_STATUSES = ['REGISTERED', 'ONBOARDING', 'CONVERTED', 'CHURNED'] as const;
export type ReferralStatus = (typeof REFERRAL_STATUSES)[number];

export const COMMISSION_STATUSES = ['PENDING', 'APPROVED', 'PAID', 'CANCELLED'] as const;
export type CommissionStatus = (typeof COMMISSION_STATUSES)[number];

/** A share of something, or a flat rupee amount. Used by both sides of a code. */
export const BENEFIT_KINDS = ['PERCENTAGE', 'FIXED'] as const;
export type BenefitKind = (typeof BENEFIT_KINDS)[number];
/** The partner's half of a code is measured the same two ways. */
export type CommissionBasis = BenefitKind;

/**
 * Where a code's commission comes from: the platform's commission matrix,
 * the code's own custom terms, or nowhere at all.
 */
export const COMMISSION_SOURCES = ['MATRIX', 'CUSTOM', 'NONE'] as const;
export type CommissionSource = (typeof COMMISSION_SOURCES)[number];

/** What the merchant's discount comes off. */
export const BENEFIT_TARGETS = [
  'REGISTRATION_FEE',
  'SUBSCRIPTION',
  'FIRST_BILLING_CYCLE',
  'SPECIFIC_PLANS',
  'OTHER_CHARGES',
] as const;

export type BenefitTarget = (typeof BENEFIT_TARGETS)[number];

export const BENEFIT_TARGET_LABELS: Record<BenefitTarget, string> = {
  REGISTRATION_FEE: 'Registration fee',
  SUBSCRIPTION: 'Subscription, every cycle',
  FIRST_BILLING_CYCLE: 'First billing cycle only',
  SPECIFIC_PLANS: 'Named plans only',
  OTHER_CHARGES: 'Other charges',
};

/**
 * Never stored: the backend derives it from the schedule, the active flag and
 * the redemption count, so it cannot be set from the editor.
 */
export const REFERRAL_CODE_STATUSES = [
  'ACTIVE',
  'SCHEDULED',
  'EXPIRED',
  'DISABLED',
  'EXHAUSTED',
] as const;

export type ReferralCodeStatus = (typeof REFERRAL_CODE_STATUSES)[number];

export const partnerSummarySchema = z.object({
  id: z.string(),
  partner_type: z.enum(PARTNER_TYPES),
  status: z.enum(PARTNER_STATUSES),
  company_name: z.string().nullable(),
  contact_name: z.string(),
  contact_email: z.string(),
  contact_phone: z.string().nullable(),
  referrals_total: z.number(),
  referrals_converted: z.number(),
  active_codes: z.number(),
  commission_total: z.string(),
  merchant_benefits_total: z.string(),
  created_at: z.string(),
  approved_at: z.string().nullable(),
});

export const partnerDetailSchema = partnerSummarySchema.extend({
  user_id: z.string(),
  website: z.string().nullable(),
  pitch: z.string().nullable(),
  payout_reference: z.string().nullable(),
  payout_notes: z.string().nullable(),
  suspended_at: z.string().nullable(),
  suspension_reason: z.string().nullable(),
  internal_notes: z.string().nullable(),
  referrals_registered: z.number(),
  referrals_onboarding: z.number(),
  referrals_churned: z.number(),
  commission_pending: z.string(),
  commission_approved: z.string(),
  commission_paid: z.string(),
});

export type PartnerSummary = z.infer<typeof partnerSummarySchema>;
export type PartnerDetail = z.infer<typeof partnerDetailSchema>;

export const partnerOverviewSchema = z.object({
  partners_total: z.number(),
  partners_pending: z.number(),
  partners_active: z.number(),
  partners_suspended: z.number(),
  referrals_total: z.number(),
  referrals_converted: z.number(),
  active_codes: z.number(),
  commission_pending: z.string(),
  commission_approved: z.string(),
  commission_paid: z.string(),
  merchant_benefits_total: z.string(),
});

export type PartnerProgrammeOverview = z.infer<typeof partnerOverviewSchema>;

export const platformCommissionSchema = z.object({
  id: z.string(),
  partner_id: z.string(),
  partner_name: z.string(),
  business_name: z.string().nullable(),
  amount: z.string(),
  base_amount: z.string(),
  currency: z.string(),
  basis: z.enum(BENEFIT_KINDS),
  rate_value: z.string(),
  status: z.enum(COMMISSION_STATUSES),
  earned_at: z.string(),
  approved_at: z.string().nullable(),
  paid_at: z.string().nullable(),
  note: z.string().nullable(),
});

export type PlatformCommission = z.infer<typeof platformCommissionSchema>;

export const platformReferralSchema = z.object({
  id: z.string(),
  partner_id: z.string(),
  partner_name: z.string(),
  code: z.string(),
  status: z.enum(REFERRAL_STATUSES),
  merchant_name: z.string().nullable().optional(),
  merchant_email: z.string().nullable().optional(),
  tenant_id: z.string().nullable(),
  business_name: z.string().nullable(),
  onboarding_step: z.string().nullable(),
  plan_code: z.string().nullable(),
  merchant_discount_total: z.string(),
  commission_total: z.string(),
  benefit_status: z.string().nullable(),
  registered_at: z.string(),
  converted_at: z.string().nullable(),
  activated_at: z.string().nullable(),
});

export type PlatformReferral = z.infer<typeof platformReferralSchema>;

/**
 * The discount block and the commission block are two separate optional halves
 * of one row. `benefit_kind` null means the code gives the merchant nothing;
 * `commission_basis` null means it earns the partner nothing. Both null is a
 * tracking-only code, which is a legitimate thing to issue.
 */
export const platformReferralCodeSchema = z.object({
  id: z.string(),
  partner_id: z.string(),
  code: z.string(),
  label: z.string().nullable(),
  status: z.enum(REFERRAL_CODE_STATUSES),
  referral_link: z.string(),
  is_active: z.boolean(),
  starts_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  max_redemptions: z.number().nullable(),
  redemption_count: z.number(),
  benefit_kind: z.enum(BENEFIT_KINDS).nullable(),
  benefit_value: z.string().nullable(),
  benefit_target: z.enum(BENEFIT_TARGETS).nullable(),
  benefit_plan_codes: z.array(z.string()).nullable(),
  benefit_max_amount: z.string().nullable(),
  benefit_min_amount: z.string().nullable(),
  benefit_max_cycles: z.number().nullable(),
  commission_basis: z.enum(BENEFIT_KINDS).nullable(),
  commission_value: z.string().nullable(),
  commission_max_amount: z.string().nullable(),
  commission_recurring: z.boolean(),
  commission_duration_months: z.number().nullable(),
  benefit_summary: z.string().nullable(),
  commission_summary: z.string().nullable(),
  /**
   * Where the partner's commission comes from. Optional because a response
   * from before the matrix carries none; the editor falls back to reading the
   * custom fields (`commissionSourceOf`).
   */
  commission_source: z.enum(COMMISSION_SOURCES).optional(),
  notes: z.string().nullable(),
  created_at: z.string(),
});

export type PlatformReferralCode = z.infer<typeof platformReferralCodeSchema>;

/** A code's commission source, inferred from its custom fields when unstated. */
export function commissionSourceOf(code: PlatformReferralCode): CommissionSource {
  if (code.commission_source) return code.commission_source;
  return code.commission_basis ? 'CUSTOM' : 'NONE';
}

export const PARTNER_SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'referrals_desc', label: 'Most referrals' },
  { value: 'commission_desc', label: 'Highest commission' },
] as const;

export type PartnerSort = (typeof PARTNER_SORTS)[number]['value'];

export interface PartnerListParams {
  page?: number;
  size?: number;
  q?: string;
  status?: PartnerStatus | '';
  type?: PartnerType | '';
  sort?: PartnerSort;
}

export function fetchPartners(params: PartnerListParams = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.size) search.set('size', String(params.size));
  if (params.q) search.set('q', params.q);
  if (params.status) search.set('status', params.status);
  if (params.type) search.set('type', params.type);
  if (params.sort) search.set('sort', params.sort);
  const query = search.toString();
  return apiRequest(
    `/platform/partners${query ? `?${query}` : ''}`,
    pageSchema(partnerSummarySchema),
  );
}

/**
 * Creating a partner creates the account behind it and emails an invite to set
 * a password, so there is no password field here and never should be.
 */
export interface PartnerCreatePayload {
  email: string;
  full_name: string;
  /**
   * Optional starting password. Omitted, the partner is emailed an invitation
   * and picks their own; sent, the account opens ready to sign in and staff
   * hand the credential over themselves.
   */
  password?: string;
  partner_type: PartnerType;
  company_name?: string | null;
  phone?: string | null;
  website?: string | null;
  status?: PartnerStatus;
  payout_reference?: string | null;
  internal_notes?: string | null;
}

export function createPartner(payload: PartnerCreatePayload) {
  return apiRequest('/platform/partners', partnerDetailSchema, {
    method: 'POST',
    body: payload,
  });
}

export function fetchPartnerOverview() {
  return apiRequest('/platform/partners/overview', partnerOverviewSchema);
}

export function fetchPartner(partnerId: string) {
  return apiRequest(`/platform/partners/${partnerId}`, partnerDetailSchema);
}

/** Everything editable about a partner that is not their status. */
export interface PartnerUpdatePayload {
  partner_type?: PartnerType;
  /** Replaces their sign-in password and ends every session they have open. */
  password?: string;
  company_name?: string | null;
  contact_phone?: string | null;
  website?: string | null;
  payout_reference?: string | null;
  payout_notes?: string | null;
  internal_notes?: string | null;
}

export function updatePartner(partnerId: string, payload: PartnerUpdatePayload) {
  return apiRequest(`/platform/partners/${partnerId}`, partnerDetailSchema, {
    method: 'PATCH',
    body: payload,
  });
}

/**
 * A reason is required to suspend or reject, and the backend enforces that.
 * The partner is told, so it is written as an explanation rather than a note.
 */
export function changePartnerStatus(
  partnerId: string,
  payload: { status: PartnerStatus; reason?: string },
) {
  return apiRequest(`/platform/partners/${partnerId}/status`, partnerDetailSchema, {
    method: 'PUT',
    body: payload,
  });
}

export function fetchPartnerReferrals(
  partnerId: string,
  params: { page?: number; size?: number; status?: ReferralStatus | '' } = {},
) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.size) search.set('size', String(params.size));
  if (params.status) search.set('status', params.status);
  const query = search.toString();
  return apiRequest(
    `/platform/partners/${partnerId}/referrals${query ? `?${query}` : ''}`,
    pageSchema(platformReferralSchema),
  );
}

/** Take back a discount that was granted in error. The merchant keeps the plan. */
export function revokeReferralBenefit(partnerId: string, referralId: string, reason: string) {
  return apiRequest(
    `/platform/partners/${partnerId}/referrals/${referralId}/revoke-benefit`,
    platformReferralSchema,
    { method: 'POST', body: { reason } },
  );
}

export function fetchPartnerCodes(partnerId: string) {
  return apiRequest(
    `/platform/partners/${partnerId}/codes`,
    z.array(platformReferralCodeSchema),
  );
}

/**
 * The writable half of a code.
 *
 * Every optional field is `| null` rather than merely absent, because clearing
 * one has to be distinguishable from leaving it alone: omitting `benefit_kind`
 * keeps the discount the code already has, sending `null` removes it.
 */
export interface ReferralCodePayload {
  label?: string | null;
  is_active?: boolean;
  starts_at?: string | null;
  expires_at?: string | null;
  max_redemptions?: number | null;
  benefit_kind?: BenefitKind | null;
  benefit_value?: string | null;
  benefit_target?: BenefitTarget | null;
  benefit_plan_codes?: string[] | null;
  benefit_max_amount?: string | null;
  benefit_min_amount?: string | null;
  benefit_max_cycles?: number | null;
  /**
   * MATRIX and NONE must send the five commission fields below as null (and
   * `commission_recurring` false); CUSTOM needs a basis and a value.
   */
  commission_source?: CommissionSource;
  commission_basis?: CommissionBasis | null;
  commission_value?: string | null;
  commission_max_amount?: string | null;
  commission_recurring?: boolean;
  commission_duration_months?: number | null;
  notes?: string | null;
}

/** The code itself is fixed once issued - links carrying it are already out. */
export interface ReferralCodeCreatePayload extends ReferralCodePayload {
  code: string;
}

export function createPartnerCode(partnerId: string, payload: ReferralCodeCreatePayload) {
  return apiRequest(`/platform/partners/${partnerId}/codes`, platformReferralCodeSchema, {
    method: 'POST',
    body: payload,
  });
}

/**
 * A full replacement of the code's terms, not a sparse patch - which is why the
 * editor always sends both blocks including their nulls. Unticking "this code
 * pays a commission" has to clear five columns, and only a replacement can say
 * so.
 */
export function updatePartnerCode(
  partnerId: string,
  codeId: string,
  payload: ReferralCodePayload,
) {
  return apiRequest(
    `/platform/partners/${partnerId}/codes/${codeId}`,
    platformReferralCodeSchema,
    { method: 'PUT', body: payload },
  );
}

/** Refused with REFERRAL_CODE_IN_USE once anybody has redeemed it; disable instead. */
export function deletePartnerCode(partnerId: string, codeId: string) {
  return apiRequest(`/platform/partners/${partnerId}/codes/${codeId}`, z.null(), {
    method: 'DELETE',
  });
}

// --- Today's Deals ---------------------------------------------------------

/**
 * A platform deal: a referral code owned by NeonCarts' own house partner, with
 * the fields the public deals page shows. It never pays a commission, and its
 * discount can only come off subscription payments for now - the setup fee is
 * not discountable yet.
 */
export const DEAL_TARGETS = ['SUBSCRIPTION', 'FIRST_BILLING_CYCLE', 'SPECIFIC_PLANS'] as const;
export type DealTarget = (typeof DEAL_TARGETS)[number];

export const platformDealSchema = platformReferralCodeSchema.extend({
  title: z.string().nullable(),
  description: z.string().nullable(),
  terms: z.string().nullable(),
  badge: z.string().nullable(),
  is_public: z.boolean(),
  is_featured: z.boolean(),
  show_in_strip: z.boolean().catch(false),
  /** Rupees off per billing cycle, e.g. `{ YEARLY: "1000.00" }`. */
  benefit_interval_values: z.record(z.string(), z.string()).nullable().catch(null),
  sort_order: z.number(),
  display_mode: z.enum(DEAL_DISPLAY_MODES),
  image_url: z.string().nullable(),
  image_alt: z.string().nullable(),
  converted_count: z.number(),
  discount_given: z.string(),
});

export type PlatformDeal = z.infer<typeof platformDealSchema>;

export interface DealPayload {
  title: string;
  description: string | null;
  terms: string | null;
  badge: string | null;
  is_public: boolean;
  is_featured: boolean;
  /** Shown in the marketing header strip. Saving it on clears it elsewhere. */
  show_in_strip: boolean;
  /** Fixed deals only; a cycle left out gets no discount. */
  benefit_interval_values: Record<string, string> | null;
  sort_order: number;
  display_mode: DealDisplayMode;
  image_url: string | null;
  image_alt: string | null;
  label: string | null;
  is_active: boolean;
  starts_at: string | null;
  expires_at: string | null;
  max_redemptions: number | null;
  benefit_kind: BenefitKind;
  benefit_value: string;
  benefit_target: DealTarget;
  benefit_plan_codes: string[] | null;
  benefit_max_amount: string | null;
  benefit_min_amount: string | null;
  benefit_max_cycles: number | null;
  notes: string | null;
}

export interface DealCreatePayload extends DealPayload {
  code: string;
}

export function fetchDeals() {
  return apiRequest('/platform/deals', z.array(platformDealSchema));
}

export function createDeal(payload: DealCreatePayload) {
  return apiRequest('/platform/deals', platformDealSchema, { method: 'POST', body: payload });
}

/** A full replacement, like `updatePartnerCode`. */
export function updateDeal(dealId: string, payload: DealPayload) {
  return apiRequest(`/platform/deals/${dealId}`, platformDealSchema, {
    method: 'PUT',
    body: payload,
  });
}

/** Refused with REFERRAL_CODE_IN_USE once anybody has redeemed it; turn it off instead. */
/**
 * Upload a deal card's picture and get back the address to save in
 * `image_url`. Nothing changes until the deal is saved, and a save accepts
 * only an address this returned (DEAL_IMAGE_NOT_UPLOADED otherwise).
 */
export async function uploadDealImage(file: File): Promise<string> {
  const body = new FormData();
  body.append('file', file);
  const stored = await apiRequest('/platform/deals/image', z.object({ url: z.string() }), {
    method: 'POST',
    body,
  });
  return stored.url;
}

/** The merchants who signed up with a deal, newest first. */
export function fetchDealRedemptions(dealId: string, params: { page?: number; size?: number } = {}) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.size) search.set('size', String(params.size));
  const query = search.toString();
  return apiRequest(
    `/platform/deals/${dealId}/redemptions${query ? `?${query}` : ''}`,
    pageSchema(platformReferralSchema),
  );
}

export function deleteDeal(dealId: string) {
  return apiRequest(`/platform/deals/${dealId}`, z.null(), { method: 'DELETE' });
}

export function fetchPartnerCommissions(
  params: {
    page?: number;
    size?: number;
    partner_id?: string;
    status?: CommissionStatus | '';
  } = {},
) {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.size) search.set('size', String(params.size));
  if (params.partner_id) search.set('partner_id', params.partner_id);
  if (params.status) search.set('status', params.status);
  const query = search.toString();
  return apiRequest(
    `/platform/partners/commissions${query ? `?${query}` : ''}`,
    pageSchema(platformCommissionSchema),
  );
}

export function changeCommissionStatus(
  commissionId: string,
  payload: { status: CommissionStatus; note?: string },
) {
  return apiRequest(
    `/platform/partners/commissions/${commissionId}/status`,
    platformCommissionSchema,
    { method: 'PUT', body: payload },
  );
}

// --- Commission matrix -----------------------------------------------------

export const COMMISSION_CYCLES = ['MONTHLY', 'QUARTERLY', 'YEARLY'] as const;
export type CommissionCycle = (typeof COMMISSION_CYCLES)[number];

/**
 * One paid plan row and what a partner earns on it. Editions are rows of
 * their own, so a row is in practice a plan family on one billing cycle.
 *
 * The rate fields are null for a plan the matrix has no rate for.
 * `renewal_months` null means renewals pay for as long as the merchant does;
 * 0 means the first payment only. Earnings are rupees on one payment at list
 * price - with no bonus on the platform read, and with the partner's current
 * bonus on theirs.
 */
export const commissionMatrixRowSchema = z.object({
  plan_id: z.string(),
  plan_code: z.string(),
  plan_name: z.string(),
  family_code: z.string(),
  family_name: z.string(),
  cycle: z.string(),
  cycle_months: z.number(),
  price: z.string(),
  currency: z.string(),
  plan_status: z.string(),
  is_public: z.boolean(),
  first_rate: z.string().nullable(),
  renewal_rate: z.string().nullable(),
  renewal_months: z.number().nullable(),
  max_amount: z.string().nullable(),
  is_active: z.boolean(),
  first_earning: z.string().nullable(),
  renewal_earning: z.string().nullable(),
});

export type CommissionMatrixRow = z.infer<typeof commissionMatrixRowSchema>;

/** A volume bonus: `bonus_rate` points on top of every rate from this many paying merchants. */
export const commissionTierSchema = z.object({
  min_paying_merchants: z.number(),
  bonus_rate: z.string(),
  label: z.string().nullable(),
});

export type CommissionTier = z.infer<typeof commissionTierSchema>;

export const commissionMatrixSchema = z.object({
  rows: z.array(commissionMatrixRowSchema),
  tiers: z.array(commissionTierSchema),
});

export type CommissionMatrix = z.infer<typeof commissionMatrixSchema>;

export interface CommissionRatePayload {
  plan_id: string;
  first_rate: string;
  renewal_rate: string;
  renewal_months: number | null;
  max_amount: string | null;
  is_active: boolean;
}

export interface CommissionTierPayload {
  min_paying_merchants: number;
  bonus_rate: string;
  label: string | null;
}

/** A full replacement: a plan left out of `rates` loses its rate, a tier left out is gone. */
export interface CommissionMatrixPayload {
  rates: CommissionRatePayload[];
  tiers: CommissionTierPayload[];
}

export function fetchCommissionMatrix() {
  return apiRequest('/platform/partners/commission-matrix', commissionMatrixSchema);
}

export function saveCommissionMatrix(payload: CommissionMatrixPayload) {
  return apiRequest('/platform/partners/commission-matrix', commissionMatrixSchema, {
    method: 'PUT',
    body: payload,
  });
}


// --- AI image credits ------------------------------------------------------

/**
 * One merchant's credit position.
 *
 * `monthly_limit` is their *plan's* allowance, not a per-merchant figure -
 * changing it for everyone on a plan is one edit on the plans screen. Only
 * `purchased_balance` belongs to the merchant, and only a grant moves it.
 */
export const merchantAiCreditsSchema = z.object({
  monthly_limit: z.number().nullable(),
  used: z.number(),
  allowance_remaining: z.number().nullable(),
  purchased_balance: z.number(),
  lifetime_purchased: z.number(),
  reset_date: z.string(),
  /** Their latest AI credit pack purchases, paid or not, newest first. */
  recent_purchases: z
    .array(
      z.object({
        id: z.string(),
        credits: z.number(),
        amount: z.coerce.string(),
        currency: z.string(),
        status: z.string(),
        failure_reason: z.string().nullable(),
        created_at: z.string(),
        paid_at: z.string().nullable(),
      }),
    )
    .optional()
    .default([]),
});
export type MerchantAiCredits = z.infer<typeof merchantAiCreditsSchema>;

export function fetchMerchantAiCredits(tenantId: string) {
  return apiRequest(`/platform/merchants/${tenantId}/ai-credits`, merchantAiCreditsSchema);
}

/**
 * Add credits to a merchant's wallet, or take them back.
 *
 * Signed: a mistaken grant is corrected by granting a negative quantity, which
 * leaves both entries in the merchant's history rather than rewriting it. The
 * server refuses one that would take the wallet below zero.
 */
export function grantMerchantAiCredits(tenantId: string, credits: number, reason: string) {
  return apiRequest(`/platform/merchants/${tenantId}/ai-credits`, merchantAiCreditsSchema, {
    method: 'POST',
    body: { credits, reason },
  });
}
